import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { supabase, traduzErro } from "./supabaseClient";

const B = {
  cafe:"#2c1810", marrom:"#4a3020", caramelo:"#8b6f4e", dourado:"#c4a478",
  creme:"#f5ebe0", offwhite:"#faf6f1", latte:"#d4a574", verde:"#6b8e5a",
  vermelho:"#b03020", laranja:"#d4783a", cinza:"#9b8e85", branco:"#ffffff"
};

function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return m;
}

const store = {
  async get(k, def) {
    try { const r = await window.storage?.get(k); if (r?.value) return JSON.parse(r.value); } catch {}
    try { const v = localStorage.getItem(k); if (v) return JSON.parse(v); } catch {}
    return def;
  },
  async set(k, v) {
    try { await window.storage?.set(k, JSON.stringify(v)); } catch {}
    try { localStorage.setItem(k, JSON.stringify(v)); } catch {}
  }
};

// ─── USERS & PERMISSIONS ──────────────────────────────────────────────────────
// Admins fixos do sistema: sempre existem, não podem ser removidos
// ─── DADOS COMPARTILHADOS (Supabase) ─────────────────────────────────────────
// Antes tudo vivia no localStorage de cada navegador, então cada máquina era uma
// ilha. Agora é uma base só. Duas decisões importantes aqui:
//
// 1. UMA LINHA POR OBRA. O app salvava o array inteiro de obras a cada mudança;
//    numa base compartilhada isso faria um usuário apagar o trabalho do outro.
//    Como toda alteração passa por handleUpdate, gravar só a obra alterada resolve.
//
// 2. Conjuntos globais (logs, contatos, templates, agendamentos) têm baixa
//    concorrência e ficam como chave-valor em app_data.
const dataStore = {
  async carregarObras() {
    const { data, error } = await supabase
      .from("obras").select("id, data").order("id", { ascending: true });
    if (error) { console.error("carregarObras:", error.message); return null; }
    // A RLS já devolve só as obras que este usuário pode ver.
    return (data || []).map(r => ({ ...r.data, id: r.id }));
  },

  async salvarObra(obra) {
    const { error } = await supabase
      .from("obras").update({ data: obra }).eq("id", obra.id);
    if (error) { console.error("salvarObra:", error.message); return { ok: false, erro: error.message }; }
    return { ok: true };
  },

  async criarObra(obra) {
    const { error } = await supabase
      .from("obras").insert({ id: obra.id, data: obra });
    if (error) { console.error("criarObra:", error.message); return { ok: false, erro: error.message }; }
    return { ok: true };
  },

  // Exclui UMA implantação. As tarefas e o checklist vivem dentro do próprio JSON
  // da obra (mesma linha), então saem junto atomicamente — não existe nem é criada
  // regra de cascata para elas. Os anexos ficam no Storage sob <obraId>/, e são
  // naturalmente dependentes: se não forem removidos, viram arquivo órfão ocupando
  // cota. Logs e contatos NÃO são tocados: referenciam a obra por nome/texto livre,
  // são registro histórico compartilhado, e apagá-los seria criar uma regra nova.
  async apagarObra(obraId) {
    // 1) anexos no Storage, dentro do prefixo desta obra e de mais nenhuma
    try {
      const caminhos = [];
      const { data: pastas } = await supabase.storage.from(BUCKET_ANEXOS).list(String(obraId));
      for (const pasta of (pastas || [])) {
        if (pasta.id === null || !pasta.name) continue; // entrada de pasta
        const { data: arqs } = await supabase.storage
          .from(BUCKET_ANEXOS).list(String(obraId) + "/" + pasta.name);
        for (const a of (arqs || [])) {
          if (a.name) caminhos.push(String(obraId) + "/" + pasta.name + "/" + a.name);
        }
      }
      if (caminhos.length) await supabase.storage.from(BUCKET_ANEXOS).remove(caminhos);
    } catch (e) {
      // Falhar aqui não impede a exclusão da obra — só deixa anexo órfão.
      console.warn("apagarObra: limpeza de anexos falhou:", e?.message);
    }

    // 2) a linha da obra. O .select() é essencial: quando a RLS recusa, o Supabase
    //    devolve sucesso com zero linhas em vez de erro. Sem isto, a interface diria
    //    "excluído" sem nada ter sido excluído.
    const { data, error } = await supabase
      .from("obras").delete().eq("id", obraId).select("id");
    if (error) return { ok: false, erro: error.message };
    if (!data || data.length === 0)
      return { ok: false, erro: "Você não tem permissão para excluir implantações. Apenas administradores podem." };
    return { ok: true };
  },

  async carregarChave(key, def) {
    const { data, error } = await supabase
      .from("app_data").select("data").eq("key", key).maybeSingle();
    if (error || !data) return def;
    return data.data ?? def;
  },

  async salvarChave(key, valor) {
    const { error } = await supabase
      .from("app_data").upsert({ key, data: valor }, { onConflict: "key" });
    if (error) console.error("salvarChave " + key + ":", error.message);
  },

  // Usado pela restauração de backup: substitui a base inteira. Só admin passa
  // pela RLS, e a interface ainda exige confirmação digitada.
  async substituirTudo({ obras, logs, contatos, templates, agend }) {
    const { data: atuais } = await supabase.from("obras").select("id");
    const idsAtuais = (atuais || []).map(r => r.id);
    const idsNovos = (obras || []).map(o => o.id);
    const remover = idsAtuais.filter(id => !idsNovos.includes(id));

    if (remover.length) {
      const { error } = await supabase.from("obras").delete().in("id", remover);
      if (error) return { ok: false, erro: error.message };
    }
    if (obras?.length) {
      const linhas = obras.map(o => ({ id: o.id, data: o }));
      const { error } = await supabase.from("obras").upsert(linhas, { onConflict: "id" });
      if (error) return { ok: false, erro: error.message };
    }
    await Promise.all([
      dataStore.salvarChave("ai-logs-v2", logs || []),
      dataStore.salvarChave("ai-contatos-v2", contatos || []),
      dataStore.salvarChave("ai-templates-v2", templates || []),
      dataStore.salvarChave("ai-agend-v2", agend || null)
    ]);
    return { ok: true };
  },

  // Snapshot da base antes de uma restauração, para haver caminho de volta.
  async snapshotAntesDeRestaurar() {
    const [obras, logs, contatos, templates, agend] = await Promise.all([
      dataStore.carregarObras(),
      dataStore.carregarChave("ai-logs-v2", []),
      dataStore.carregarChave("ai-contatos-v2", []),
      dataStore.carregarChave("ai-templates-v2", []),
      dataStore.carregarChave("ai-agend-v2", null)
    ]);
    await dataStore.salvarChave("_snapshot-pre-restauracao", {
      criadoEm: new Date().toISOString(), obras, logs, contatos, templates, agend
    });
    return { obras: obras?.length || 0 };
  }
};

// Contatos exibidos na tela de "aguardando aprovação". Só para o usuário saber a
// quem pedir liberação — nenhuma senha, nenhum privilégio vem daqui. Quem é admin
// de verdade está na coluna role da tabela profiles.
const ADMIN_CONTATOS = [
  { nome: "Lucas",    email: "lucas@pontoacafe.com.br" },
  { nome: "Israel",   email: "israel.daywis@gmail.com" },
  { nome: "Vinicius", email: "viniciusalveswork@outlook.com" },
  { nome: "Ricardo",  email: "implantacao@pontoacafe.com.br" }
];

// ─── USUÁRIOS & PERMISSÕES (Supabase) ────────────────────────────────────────
// As senhas NÃO existem mais neste arquivo. Elas vivem no auth.users do Supabase,
// com hash, e nunca chegam ao navegador. Os administradores são definidos na
// coluna role da tabela profiles, e protegidos por is_protected.
//
// Formato do objeto de usuário — IDÊNTICO ao anterior, de propósito: assim todo
// o resto do app (Sidebar, ObraDetail, permissões, modo visitante) continua
// funcionando sem alteração.
// { id, nome, email, role, status, permissoes:{todas,obras,viewOnly},
//   mustChangePassword, isFixed, createdAt, approvedBy, approvedAt }

function perfilParaUsuario(p) {
  if (!p) return null;
  return {
    id: p.id,
    nome: p.nome || "",
    email: p.email,
    role: p.role,
    status: p.status,
    permissoes: p.permissoes || { todas: false, obras: [], viewOnly: true },
    mustChangePassword: !!p.must_change_password,
    isFixed: !!p.is_protected,
    createdAt: p.created_at,
    approvedBy: p.approved_by,
    approvedAt: p.approved_at
  };
}

const userStore = {
  // Lista todos os perfis. A RLS garante que só admin recebe mais de um.
  async getAll() {
    const { data, error } = await supabase
      .from("profiles").select("*").order("created_at", { ascending: true });
    if (error) { console.error("getAll", error.message); return []; }
    return (data || []).map(perfilParaUsuario);
  },

  // Perfil do usuário logado.
  async getCurrent() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const { data, error } = await supabase
      .from("profiles").select("*").eq("id", session.user.id).maybeSingle();
    if (error || !data) return null;
    return perfilParaUsuario(data);
  },

  // Cadastro. O perfil é criado pelo gatilho do banco, sempre como pendente.
  async add(nome, email, senha) {
    const { error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password: senha,
      options: { data: { nome: nome.trim() } }
    });
    if (error) return { ok: false, erro: traduzErro(error.message) };
    // Encerra a sessão criada automaticamente: o acesso só vale após aprovação.
    await supabase.auth.signOut();
    return { ok: true };
  },

  // Login. Devolve o perfil; quem checa status é a tela de login.
  async authenticate(email, senha) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password: senha.trim()
    });
    if (error) return { ok: false, erro: traduzErro(error.message) };

    const { data: perfil } = await supabase
      .from("profiles").select("*").eq("id", data.user.id).maybeSingle();

    if (!perfil) {
      await supabase.auth.signOut();
      return { ok: false, erro: "Sua conta não tem perfil no sistema. Contate um administrador." };
    }
    if (perfil.status === "bloqueado") {
      await supabase.auth.signOut();
      return { ok: false, erro: "Sua conta foi bloqueada. Contate um administrador." };
    }
    return { ok: true, user: perfilParaUsuario(perfil) };
  },

  // Altera campos do perfil. A RLS e os gatilhos decidem o que é permitido.
  async update(id, updates) {
    const patch = {};
    if ("nome" in updates)       patch.nome = updates.nome;
    if ("role" in updates)       patch.role = updates.role;
    if ("status" in updates)     patch.status = updates.status;
    if ("permissoes" in updates) patch.permissoes = updates.permissoes;
    if ("mustChangePassword" in updates) patch.must_change_password = updates.mustChangePassword;
    if ("approvedBy" in updates) patch.approved_by = updates.approvedBy;
    if ("approvedAt" in updates) patch.approved_at = updates.approvedAt;

    const { error } = await supabase.from("profiles").update(patch).eq("id", id);
    if (error) return { ok: false, erro: traduzErro(error.message) };
    return { ok: true };
  },

  // Remove o acesso apagando o perfil. A conta em auth.users permanece — apagá-la
  // exige chave secreta, que não pode existir no navegador. Sem perfil, o login é
  // recusado, então o efeito prático é o mesmo.
  async remove(id) {
    const { error } = await supabase.from("profiles").delete().eq("id", id);
    if (error) return { ok: false, erro: traduzErro(error.message) };
    return { ok: true };
  },

  // Envia email de redefinição. Substitui a antiga senha temporária: um admin não
  // pode definir a senha de outra pessoa sem chave secreta — e não deveria.
  async enviarRedefinicao(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: window.location.origin }
    );
    if (error) return { ok: false, erro: traduzErro(error.message) };
    return { ok: true };
  },

  // Troca a senha do próprio usuário logado.
  async trocarSenha(novaSenha) {
    const { error } = await supabase.auth.updateUser({ password: novaSenha });
    if (error) return { ok: false, erro: traduzErro(error.message) };
    await supabase.from("profiles")
      .update({ must_change_password: false })
      .eq("id", (await supabase.auth.getSession()).data.session?.user?.id);
    return { ok: true };
  },

  async countActiveAdmins() {
    const { count, error } = await supabase
      .from("profiles").select("id", { count: "exact", head: true })
      .eq("role", "admin").eq("status", "aprovado");
    if (error) return 0;
    return count || 0;
  },

  async logout() {
    await supabase.auth.signOut();
  }
};

// Log de ações administrativas — agora compartilhado entre todos os admins.
const adminLogStore = {
  async getAll() {
    const { data, error } = await supabase
      .from("admin_logs").select("*").order("created_at", { ascending: false }).limit(500);
    if (error) return [];
    return (data || []).map(r => ({
      id: r.id, ts: r.created_at, actor: r.actor,
      action: r.acao, target: r.alvo, detail: r.detalhe || ""
    }));
  },
  async add(actorEmail, action, targetEmail, detail = "") {
    await supabase.from("admin_logs").insert({
      actor: actorEmail, acao: action, alvo: targetEmail, detalhe: detail
    });
  }
};

// Lembra apenas o EMAIL para preencher a tela de login. A senha nunca é guardada —
// a versão anterior gravava a senha em texto puro no localStorage, e quem abrisse o
// painel do navegador conseguia lê-la. A sessão em si é mantida pelo Supabase.
const sessionStore = {
  saveEmail(email) {
    try { localStorage.setItem("ai-last-email", String(email || "").trim().toLowerCase()); } catch {}
  },
  loadEmail() {
    try { return localStorage.getItem("ai-last-email") || ""; } catch { return ""; }
  },
  clear() {
    try { localStorage.removeItem("ai-last-email"); } catch {}
    // limpa resíduo da versão antiga, que continha senha em texto puro
    try { localStorage.removeItem("ai-session-v1"); } catch {}
  }
};

// Permission helpers
const isAdmin = (u) => u && u.role === "admin";
const canEdit = (u) => u && !u.isVisitor && (isAdmin(u) || (u.permissoes && !u.permissoes.viewOnly));
const canAccessObra = (u, obraId) => {
  if (!u) return false;
  if (u.isVisitor) return true;
  if (isAdmin(u)) return true;
  if (!u.permissoes) return false;
  if (u.permissoes.todas) return true;
  return (u.permissoes.obras || []).includes(obraId);
};
const visibleObras = (u, obras) => {
  if (!u) return [];
  if (u.isVisitor) return obras;
  if (isAdmin(u) || (u.permissoes && u.permissoes.todas)) return obras;
  return obras.filter(o => (u.permissoes?.obras || []).includes(o.id));
};

// Sanitiza dados sensíveis para o modo visitante (sem nomes, CPFs, telefones, custos, arquivos)
const sanitizeForVisitor = (obras) => obras.map(o => ({
  ...o,
  resp: "—",
  franqueado: { nome: "—", cpf: "—", telefone: "", email: "" },
  contato2: { nome: "—", cargo: "—", telefone: "", email: "" },
  contato3: { nome: "—", cargo: "—", telefone: "", email: "" },
  tarefas: o.tarefas.map(t => ({ ...t, responsavel: "—", comentario: "", custo_previsto: 0, custo_realizado: 0, arquivos: [] })),
  arquivos: [],
  cp: 0, cr: 0
}));

// Sessão "lembrar acesso"

const TT = [
  {id:1,p:1,t:"Buscar e selecionar ponto comercial",w:0.02,s:0,d:5,dep:null},
  {id:2,p:2,t:"Projeto da Loja / Layout",w:0.02,s:7,d:5,dep:1},
  {id:3,p:2,t:"Visita técnica (medidas e elétrica)",w:0.02,s:9,d:3,dep:2},
  {id:4,p:2,t:"Assinatura do contrato c/ franqueado",w:0.02,s:11,d:2,dep:3},
  {id:5,p:2,t:"Contrato de franquia / CNPJ",w:0.02,s:13,d:2,dep:4},
  {id:6,p:3,t:"Abertura da Empresa e CNPJ",w:0.02,s:20,d:7,dep:5},
  {id:7,p:3,t:"Cotação Quiosque / Loja",w:0.02,s:22,d:5,dep:5},
  {id:8,p:3,t:"Projeto arquitetônico",w:0.02,s:24,d:7,dep:3},
  {id:9,p:3,t:"Projeto elétrico",w:0.02,s:26,d:5,dep:8},
  {id:10,p:4,t:"Contratar comunicação visual",w:0.02,s:33,d:5,dep:8},
  {id:11,p:4,t:"Conta Bancária",w:0.02,s:35,d:3,dep:6},
  {id:12,p:4,t:"Aquisição Máquinas de Cartão",w:0.02,s:37,d:3,dep:11},
  {id:13,p:4,t:"Documentação ANVISA / Prefeitura",w:0.02,s:39,d:10,dep:6},
  {id:14,p:4,t:"Comprar uniformes para equipe",w:0.02,s:41,d:3,dep:null},
  {id:15,p:4,t:"Reforma civil / pré-montagem",w:0.04,s:45,d:14,dep:20},
  {id:16,p:4,t:"Aquisição móveis e equipamentos",w:0.03,s:45,d:7,dep:7},
  {id:17,p:4,t:"Comprar Logo e luminosos",w:0.02,s:47,d:5,dep:10},
  {id:18,p:4,t:"Instalação de bombona para água",w:0.02,s:59,d:2,dep:15},
  {id:19,p:4,t:"Aprovação dos projetos",w:0.03,s:31,d:7,dep:9},
  {id:20,p:4,t:"Aprovação APT / Autorização instalação",w:0.03,s:38,d:7,dep:19},
  {id:21,p:4,t:"Equipamentos sob medida",w:0.03,s:55,d:10,dep:7},
  {id:22,p:4,t:"Instalação elétrica",w:0.03,s:59,d:5,dep:15},
  {id:23,p:4,t:"Montagem Loja / Quiosque",w:0.04,s:64,d:7,dep:22},
  {id:24,p:4,t:"Solicitar equipamentos comodato CD",w:0.02,s:61,d:3,dep:null},
  {id:25,p:4,t:"Levantamento utensílios equipe",w:0.02,s:61,d:2,dep:null},
  {id:26,p:5,t:"Solicitar ligação do relógio elétrico",w:0.02,s:65,d:3,dep:15},
  {id:27,p:5,t:"Instalação da internet",w:0.02,s:71,d:2,dep:23},
  {id:28,p:5,t:"Instalar câmeras de segurança",w:0.02,s:73,d:2,dep:27},
  {id:29,p:5,t:"Comprar cofre, extintores, carrinho",w:0.02,s:69,d:2,dep:null},
  {id:30,p:5,t:"Configuração do sistema de vendas",w:0.02,s:73,d:3,dep:27},
  {id:31,p:5,t:"Cadastro fornecedores / estoque",w:0.02,s:73,d:3,dep:null},
  {id:32,p:5,t:"Contratação e seleção da equipe",w:0.04,s:70,d:7,dep:null},
  {id:33,p:5,t:"Treinamento operacional completo",w:0.04,s:77,d:5,dep:32},
  {id:34,p:5,t:"Primeiro pedido de produtos",w:0.02,s:79,d:3,dep:31},
  {id:35,p:6,t:"Ambientação e decoração final",w:0.02,s:82,d:3,dep:23},
  {id:36,p:6,t:"Vistoria final e checklist abertura",w:0.03,s:84,d:2,dep:35},
  {id:37,p:7,t:"Inauguração da unidade",w:0.05,s:87,d:1,dep:36},
];

const INAUG = [
  "Loja limpa e organizada","Equipamentos testados","Sistema de vendas ativo",
  "Estoque inicial completo","Uniformes prontos","Vigilância sanitária aprovada",
  "Internet e câmeras ok","Comunicação visual instalada","Fotos da loja registradas",
  "Post redes sociais agendado","Inauguração oficial realizada"
];

const CB = {1:5000,2:8000,3:3000,4:2000,5:2000,6:3000,7:12000,8:6000,9:4000,10:8000,11:500,12:1500,13:2000,14:3000,15:35000,16:40000,17:6000,18:1500,19:1000,20:500,21:15000,22:8000,23:5000,24:2000,25:500,26:1000,27:1200,28:3500,29:2000,30:1500,31:500,32:5000,33:4000,34:8000,35:3000,36:500,37:2000};

const TODAY = new Date("2026-04-13");

const statusColor = (s) => ({
  "Concluído": B.verde, "Concluída": B.verde, "No Prazo": B.verde,
  "Em Andamento": B.latte, "Em Risco": B.laranja,
  "Atrasado": B.vermelho, "Atrasada": B.vermelho,
  "Bloqueado": B.cinza, "Não Iniciado": B.caramelo, "Não se aplica": B.cinza
}[s] || B.caramelo);

const statusBg = (s) => ({
  "Concluído": "#e8f5e1", "Concluída": "#e8f5e1", "No Prazo": "#e8f5e1",
  "Em Andamento": "#fff8ee", "Em Risco": "#fff0e0",
  "Atrasado": "#fdecea", "Atrasada": "#fdecea",
  "Não Iniciado": B.creme, "Bloqueado": "#f0edf8", "Não se aplica": "#eae7e3"
}[s] || B.creme);

const prioColor = (p) => [B.vermelho, B.laranja, B.latte, B.caramelo, B.verde, B.cinza, B.marrom][p - 1] || B.caramelo;
const fBRL = (v) => "R$\u00A0" + Math.round(v || 0).toLocaleString("pt-BR");

function computeStatus(tk, prog, elap) {
  if (prog === 100) return "Concluído";
  const end = tk.s + tk.d;
  if (prog > 0 && elap > end) return "Atrasado";
  if (prog > 0) return "Em Andamento";
  if (elap > end) return "Atrasado";
  return "Não Iniciado";
}

function isBlocked(tk, tarefas) {
  if (!tk.dep) return false;
  const dep = tarefas.find(t => t.id === tk.dep);
  return dep && dep.status !== "Concluído" && tk.progresso === 0;
}

function daysLeft(tk, elap) { return (tk.s + tk.d) - elap; }

function recalc(obra) {
  const tw = obra.tarefas.reduce((a, t) => a + t.w, 0);
  const avanco = Math.round(obra.tarefas.reduce((a, t) => a + t.w * (t.progresso / 100), 0) / tw * 100);
  const conc = obra.tarefas.filter(t => t.status === "Concluído").length;
  const emAnd = obra.tarefas.filter(t => t.status === "Em Andamento").length;
  const atras = obra.tarefas.filter(t => t.status === "Atrasado").length;
  const naoIn = obra.tarefas.filter(t => t.status === "Não Iniciado").length;
  const base = new Date(obra.dataBase);
  const elap = Math.floor((TODAY - base) / 86400000);
  const expP = Math.min(100, elap / 88 * 100);
  const idp = expP > 0 ? Math.round(avanco / expP * 100) / 100 : 1.00;
  const ppc = Math.round(conc / obra.tarefas.length * 100);
  let oS = "No Prazo";
  if (atras > 3 || idp < 0.80) oS = "Atrasada";
  else if (atras > 0 || idp < 0.95) oS = "Em Risco";
  if (conc === obra.tarefas.length) oS = "Concluída";
  const cp = obra.tarefas.reduce((a, t) => a + (t.custo_previsto || 0), 0);
  const cr = obra.tarefas.reduce((a, t) => a + (t.custo_realizado || 0), 0);
  return { ...obra, avanco, conc, emAnd, atras, naoIn, elap, total: 88, idp, ppc, obraStatus: oS, cp, cr };
}

function genHistorico(baseDateStr, prog) {
  const base = new Date(baseDateStr);
  const tw = TT.reduce((a, t) => a + t.w, 0);
  const wks = Math.max(1, Math.floor((TODAY - base) / (7 * 86400000)));
  return Array.from({ length: wks }, (_, i) => {
    const frac = Math.min(1, (i + 1) / wks);
    const sp = prog.map(p => Math.round(p * frac));
    const av = Math.round(TT.reduce((a, t, j) => a + t.w * (sp[j] || 0) / 100, 0) / tw * 100);
    return { semana: "S" + (i + 1), avanco: av };
  });
}

function makeObra(id, nome, cidade, endereco, baseDateStr, resp, prog, ex = {}) {
  const base = new Date(baseDateStr);
  const elap = Math.floor((TODAY - base) / 86400000);
  const tarefas = TT.map((tk, i) => ({
    ...tk,
    progresso: prog[i] || 0,
    status: computeStatus(tk, prog[i] || 0, elap),
    responsavel: resp,
    comentario: "",
    custo_previsto: CB[tk.id] || 1000,
    custo_realizado: (prog[i] || 0) > 0 ? Math.round((CB[tk.id] || 1000) * (prog[i] || 0) / 100) : 0
  }));
  const o = {
    id, nome, cidade, endereco, resp, dataBase: baseDateStr,
    franqueado: ex.franqueado || { nome: "", cpf: "", telefone: "", email: "" },
    contato2: ex.contato2 || { nome: "", cargo: "Gerente de Obra", telefone: "", email: "" },
    contato3: ex.contato3 || { nome: "", cargo: "Arquiteto / Engenheiro", telefone: "", email: "" },
    tarefas,
    historico: ex.historico || genHistorico(baseDateStr, prog),
    inauguracao: INAUG.map((t, i) => ({ id: i + 1, t, done: false })),
    obs: ""
  };
  return recalc(o);
}

// Lista de templates selecionáveis: o "Template Padrão" (id 0) é sintético — mesma lista mostrada em Configurações → Templates
function getTemplateOptions(templates) {
  return [{ id: 0, nome: "Template Padrão", descricao: "37 tarefas oficiais Ponto Alpha", tarefas: TT }, ...templates];
}

// Normaliza o texto livre da coluna "Status" da planilha para um dos status canônicos do
// sistema. Usado SOMENTE como status inicial de tarefas novas — nunca sobrescreve o status
// de uma tarefa que já existe (ver applyTemplateToObra).
function normalizeTemplateStatus(raw) {
  const v = String(raw || "").normalize("NFD").replace(new RegExp("[\\u0300-\\u036f]", "g"), "").trim().toLowerCase();
  if (!v) return "Não Iniciado";
  if (v.includes("conclu")) return "Concluído";
  if (v.includes("atras")) return "Atrasado";
  if (v.includes("andamento")) return "Em Andamento";
  return "Não Iniciado";
}

// Extrai os campos de uma tarefa de template (colunas da planilha: Item, Descrição / Detalhamento,
// Responsável, Status, Data Prevista, Data Conclusão, Observações, Área) + campos internos de
// agendamento/peso (mantidos com defaults neutros — não vêm da planilha, mas o Gantt/priorização
// dependem deles). Campos ausentes/vazios na planilha viram "" (nunca undefined, nunca erro, nunca valor fictício).
function normalizeTemplateTask(tk) {
  return {
    id: tk.id,
    t: tk.t || "",
    bloco: tk.bloco || "",
    descricao: tk.descricao || "",
    responsavel: tk.responsavel || "",
    statusTemplate: normalizeTemplateStatus(tk.status),
    dataPrevista: tk.dataPrevista || "",
    dataConclusao: tk.dataConclusao || "",
    observacoes: tk.observacoes || "",
    area: tk.area || "",
    p: tk.p ?? 1, w: tk.w ?? 0.02, s: tk.s ?? 0, d: tk.d ?? 5, dep: tk.dep ?? null
  };
}

// Normaliza o título de uma tarefa (coluna "Item") apenas para fins de CORRESPONDÊNCIA entre
// o template antigo e o novo (tolera diferença de maiúsculas/minúsculas, acentos e espaços
// extras) — nunca altera o texto exibido, que continua sendo o valor real de "Item".
function normalizeMatchKey(text) {
  return String(text || "").normalize("NFD").replace(new RegExp("[\\u0300-\\u036f]", "g"), "").toLowerCase().trim().replace(/\s+/g, " ");
}

// Aplica/troca o template de tarefas de uma implantação já existente. A troca é uma
// SINCRONIZAÇÃO: o novo template define o conjunto final de tarefas e seus dados; a
// correspondência com as tarefas antigas é feita pelo título ("Item"), não pelo Nº/id
// (a mesma tarefa pode mudar de Nº entre uma versão e outra do template).
// Regra crítica: quando uma tarefa do novo template corresponde (mesmo "Item") a uma tarefa
// que já existia, o andamento é preservado — status, progresso, comentário, custos e arquivos
// vêm exclusivamente da tarefa antiga, mesmo que a planilha tenha uma coluna "Status" própria
// (essa coluna só é usada como status inicial de tarefas realmente novas). Os demais dados
// (título, bloco, descrição, responsável, datas, observações, área, agendamento/peso) são
// sempre atualizados a partir do template. Tarefas do template antigo sem correspondência no novo
// são removidas da implantação; tarefas do novo template sem correspondência são criadas.
function applyTemplateToObra(obra, template) {
  const existingByTitle = new Map();
  obra.tarefas.forEach(t => {
    const key = normalizeMatchKey(t.t);
    if (key) existingByTitle.set(key, t);
  });
  const tarefas = template.tarefas.map(raw => {
    const tpl = normalizeTemplateTask(raw);
    const key = normalizeMatchKey(tpl.t);
    const existente = key ? existingByTitle.get(key) : undefined;
    if (existente) {
      existingByTitle.delete(key); // consome a correspondência: um 2º "Item" igual no template vira tarefa nova, não duplica o mesmo andamento
      return {
        ...existente,
        id: tpl.id, t: tpl.t, bloco: tpl.bloco, descricao: tpl.descricao, responsavel: tpl.responsavel,
        dataPrevista: tpl.dataPrevista, dataConclusao: tpl.dataConclusao,
        observacoes: tpl.observacoes, area: tpl.area,
        p: tpl.p, w: tpl.w, s: tpl.s, d: tpl.d, dep: tpl.dep
      };
    }
    return {
      id: tpl.id, t: tpl.t, bloco: tpl.bloco, p: tpl.p, w: tpl.w, s: tpl.s, d: tpl.d, dep: tpl.dep,
      descricao: tpl.descricao, responsavel: tpl.responsavel,
      dataPrevista: tpl.dataPrevista, dataConclusao: tpl.dataConclusao,
      observacoes: tpl.observacoes, area: tpl.area,
      progresso: 0,
      status: tpl.statusTemplate,
      comentario: "",
      custo_previsto: CB[tpl.id] || 1000,
      custo_realizado: 0
    };
  });
  return { ...obra, tarefas, templateId: template.id, templateName: template.nome };
}

const OBRAS_DEFAULT = [
  makeObra(1, "Brasília — Rodoviária Plano Piloto", "Brasília-DF", "Rodoviária Plano Piloto, Loja B-07", "2026-02-03", "Baruque Oliveira",
    [100,100,100,100,100,100,100,100,100,100,100,100,40,100,60,70,50,0,100,60,30,0,0,80,100,0,0,0,0,0,0,0,0,0,0,0,0],
    { franqueado: { nome: "Ricardo Souza", cpf: "123.456.789-00", telefone: "(61) 99988-7766", email: "ricardo@email.com" },
      contato2: { nome: "Leandro Alves", cargo: "Engenheiro", telefone: "(61) 98877-6655", email: "" },
      contato3: { nome: "Ana Paula", cargo: "Arquiteta", telefone: "(61) 97766-5544", email: "" } }),
  makeObra(2, "Uberlândia — Shopping Iguatemi", "Uberlândia-MG", "Av. Rondon Pacheco 4600, Piso L2", "2026-02-22", "Licia Fernandes",
    [100,100,100,100,100,100,100,80,60,0,40,0,0,100,0,0,0,0,90,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0]),
  makeObra(3, "São Paulo — Tatuapé", "São Paulo-SP", "Rua Melo Freire 420, Piso Térreo", "2026-03-15", "Thiago Martins",
    [100,100,100,100,60,30,20,0,0,0,0,0,0,50,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0]),
];

async function callAI(prompt) {
  const r = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 900, messages: [{ role: "user", content: prompt }] })
  });
  const d = await r.json();
  return d.content?.find(b => b.type === "text")?.text || "Sem resposta.";
}

function exportCSV() {
  const hdr = ["Nº","Bloco","Item","Descrição / Detalhamento","Responsável","Status","Data Prevista","Data Conclusão","Observações","Área"];
  const rows = TT.map(t => [t.id, "", '"' + t.t + '"', "", "", "", "", "", "", ""].join(","));
  const a = document.createElement("a");
  a.href = "data:text/csv;charset=utf-8," + encodeURIComponent("\uFEFF" + [hdr.join(","), ...rows].join("\n"));
  a.download = "modelo_implantacao.csv";
  a.click();
}

function printPDF(obra, aiText) {
  const w = window.open("", "_blank");
  if (!w) { alert("Permita pop-ups para exportar PDF."); return; }
  const c = statusColor(obra.obraStatus);
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Relatório — ${obra.nome}</title>
  <style>body{font-family:'Montserrat',sans-serif;padding:36px;max-width:760px;margin:0 auto;color:#2c1810}
  h1{border-bottom:3px solid #c4a478;padding-bottom:10px}h2{color:#4a3020;margin-top:24px}
  .badge{display:inline-block;padding:3px 12px;border-radius:12px;font-size:12px;font-weight:bold;background:${statusBg(obra.obraStatus)};color:${c}}
  .g{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:16px 0}
  .k{background:#faf6f1;border-radius:8px;padding:12px;text-align:center}
  .kv{font-size:24px;font-weight:bold}.kl{font-size:10px;color:#8b6f4e;text-transform:uppercase}
  table{width:100%;border-collapse:collapse;font-size:12px;margin-top:10px}
  th{background:#2c1810;color:#f5ebe0;padding:6px 10px;text-align:left}
  td{padding:6px 10px;border-bottom:1px solid #f5ebe0}tr:nth-child(even){background:#faf6f1}
  @media print{body{padding:20px}}</style></head><body>
  <h1>Relatório de Implantação</h1>
  <p><strong>${obra.nome}</strong> &nbsp; <span class="badge">${obra.obraStatus}</span></p>
  <p>${obra.cidade} · Responsável: ${obra.resp}</p>
  <div class="g">
    <div class="k"><div class="kv">${obra.avanco}%</div><div class="kl">Avanço</div></div>
    <div class="k"><div class="kv">${obra.ppc}%</div><div class="kl">PPC</div></div>
    <div class="k"><div class="kv">${obra.idp}</div><div class="kl">IDP</div></div>
    <div class="k"><div class="kv">${obra.conc}/${obra.tarefas.length}</div><div class="kl">Concluídas</div></div>
  </div>
  ${aiText ? "<h2>Análise da IA</h2><p style='white-space:pre-wrap;line-height:1.7'>" + aiText + "</p>" : ""}
  <h2>Tarefas</h2>
  <table><tr><th>P</th><th>Tarefa</th><th>Status</th><th>%</th></tr>
  ${obra.tarefas.map(t => "<tr><td>P" + t.p + "</td><td>" + t.t + "</td><td>" + t.status + "</td><td>" + t.progresso + "%</td></tr>").join("")}
  </table>
  <p style="margin-top:36px;font-size:10px;color:#9b8e85">Alpha Implantações · ${new Date().toLocaleDateString("pt-BR")}</p>
  </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 500);
}

// ─── BASE UI ─────────────────────────────────────────────────────────────────
function Badge({ s }) {
  return (
    <span style={{ background: statusBg(s), color: statusColor(s), padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, border: "1px solid " + statusColor(s) + "44", whiteSpace: "nowrap", display: "inline-block" }}>
      {s}
    </span>
  );
}

function Bar({ pct, color, h = 6 }) {
  return (
    <div style={{ background: B.creme, borderRadius: 999, height: h, overflow: "hidden" }}>
      <div style={{ width: Math.min(100, Math.max(0, pct)) + "%", height: "100%", background: color || B.caramelo, borderRadius: 999, transition: "width .4s" }} />
    </div>
  );
}

function KpiCard({ label, value, icon, color, sub }) {
  return (
    <div style={{ background: B.branco, borderRadius: 12, padding: "14px", flex: "1 1 120px", minWidth: 0, border: "1px solid " + B.creme, boxShadow: "0 1px 4px rgba(44,24,16,.04)" }}>
      <div style={{ fontSize: 18, marginBottom: 2 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || B.cafe, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: B.cinza, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function Btn({ onClick, color, children, full, small, disabled, outline }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? "6px 12px" : "10px 18px", borderRadius: 8,
      border: outline ? "2px solid " + (color || B.cafe) : "none",
      background: disabled ? B.cinza : outline ? "white" : (color || B.cafe),
      color: outline ? (color || B.cafe) : B.creme,
      cursor: disabled ? "not-allowed" : "pointer", fontWeight: 700, fontSize: small ? 11 : 13,
      width: full ? "100%" : "auto", display: "inline-flex", alignItems: "center", gap: 5,
      fontFamily: "'Montserrat',sans-serif", minHeight: small ? 32 : 42, justifyContent: "center"
    }}>
      {children}
    </button>
  );
}

function Inp({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + B.creme, fontSize: 13, background: B.offwhite, boxSizing: "border-box", fontFamily: "'Montserrat',sans-serif", minHeight: 42 }} />
    </div>
  );
}

function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 2, msOverflowStyle: "none", scrollbarWidth: "none" }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: "8px 14px", borderRadius: 8, border: "2px solid " + (active === t.id ? B.cafe : B.creme),
          background: active === t.id ? B.cafe : "white", color: active === t.id ? B.creme : B.caramelo,
          cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", minHeight: 38, flexShrink: 0,
          fontFamily: "'Montserrat',sans-serif"
        }}>{t.l}</button>
      ))}
    </div>
  );
}

// ─── TEMPLATE SELECTOR (Dashboard e Detalhe da Implantação) ───────────────────
function TemplateSelector({ obra, templates, onApply, readOnly, dark }) {
  const options = getTemplateOptions(templates);
  const currentId = obra.templateId ?? 0;
  const current = options.find(t => t.id === currentId) || options[0];
  const [pending, setPending] = useState(null);
  const labelColor = dark ? B.dourado : B.cinza;
  const textColor = dark ? B.creme : B.cafe;

  return (
    <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontSize: 10, color: labelColor, fontFamily: "'Montserrat',sans-serif" }}>📋 Template:</span>
      {readOnly ? (
        <span style={{ fontSize: 10, fontWeight: 700, color: textColor, fontFamily: "'Montserrat',sans-serif" }}>{current.nome}</span>
      ) : (
        <select
          value={currentId}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const id = Number(e.target.value);
            if (id === currentId) return;
            const tpl = options.find(t => t.id === id);
            if (tpl) setPending(tpl);
          }}
          style={{ fontSize: 10, fontWeight: 700, padding: "4px 7px", borderRadius: 6, border: "1px solid " + (dark ? B.dourado + "66" : B.creme), background: dark ? "rgba(255,255,255,.08)" : B.offwhite, color: textColor, fontFamily: "'Montserrat',sans-serif", cursor: "pointer", minHeight: 28 }}
        >
          {options.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
      )}

      {pending && (
        <div onClick={e => { e.stopPropagation(); setPending(null); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: B.branco, borderRadius: 14, padding: 20, maxWidth: 380, width: "100%" }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, fontWeight: 800, color: B.cafe, textAlign: "center", marginBottom: 10 }}>Alterar template da implantação?</div>
            <div style={{ fontSize: 12, color: B.cinza, marginBottom: 16, lineHeight: 1.6, fontFamily: "'Montserrat',sans-serif" }}>
              Ao confirmar, as tarefas serão sincronizadas com o template <strong>{pending.nome}</strong>: tarefas que continuam existindo (mesmo título) mantêm seu andamento, tarefas novas do template são criadas, e tarefas que não fazem mais parte do template são removidas.
              <br /><br />
              <strong style={{ color: B.cafe }}>O Status e o Progresso das tarefas que continuam existindo serão preservados.</strong>
              <br /><br />
              Deseja realmente continuar?
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn outline color={B.cinza} onClick={() => setPending(null)}>Cancelar</Btn>
              <Btn full color={B.verde} onClick={() => { onApply(pending); setPending(null); }}>Confirmar alteração</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PASSWORD INPUT (com toggle de visualização) ──────────────────────────────
function PasswordInput({ value, onChange, placeholder, onEnter, dark }) {
  const [show, setShow] = useState(false);
  const bg = dark ? "rgba(250,246,241,.06)" : B.offwhite;
  const border = dark ? "1px solid rgba(196,164,120,.22)" : "1px solid " + B.creme;
  const color = dark ? "#f5ebe0" : B.cafe;
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => onEnter && e.key === "Enter" && onEnter()}
        placeholder={placeholder}
        style={{ width: "100%", padding: "12px 44px 12px 14px", borderRadius: 10, border, background: bg, fontSize: 13, color, fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box", outline: "none" }}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        title={show ? "Ocultar senha" : "Mostrar senha"}
        style={{
          position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
          background: "transparent", border: "none", cursor: "pointer",
          padding: "8px 10px", fontSize: 16, color: dark ? "rgba(196,164,120,.7)" : B.caramelo,
          lineHeight: 1
        }}
      >
        {show ? "🙈" : "👁"}
      </button>
    </div>
  );
}

// ─── FILE HELPERS ────────────────────────────────────────────────────────────
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB — o Storage substituiu o teto de ~5 MB do localStorage

const fileTypeGroup = (mime) => {
  if (!mime) return "other";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime === "application/pdf") return "pdf";
  return "other";
};

const fileIcon = (mime) => ({ image: "🖼️", video: "🎥", pdf: "📄", other: "📎" }[fileTypeGroup(mime)]);
const fBytes = (b) => b < 1024 ? b + " B" : b < 1048576 ? (b / 1024).toFixed(1) + " KB" : (b / 1048576).toFixed(1) + " MB";

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ─── FILE VIEWER MODAL ───────────────────────────────────────────────────────
// ─── ANEXOS NO STORAGE ───────────────────────────────────────────────────────
// Os arquivos saem do base64 dentro do JSON da obra e vão para o bucket "anexos".
// O caminho é <obraId>/<escopo>/<uuid>-<nome>, e a primeira pasta é o que a
// política do banco usa para decidir quem pode ver: a permissão do anexo é a
// mesma da obra.
//
// COMPATIBILIDADE: arquivos antigos têm o campo `data` (base64) e continuam
// funcionando sem conversão. Os novos têm `path`. O visualizador aceita os dois,
// então o backup do Ricardo — que carrega base64 — abre normalmente.
const BUCKET_ANEXOS = "anexos";

const fileStore = {
  // Nome seguro: sem acento, sem espaço, sem caractere que o Storage recuse.
  nomeSeguro(nome) {
    const limpo = String(nome || "arquivo")
      .normalize("NFD").replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
      .replace(/[^a-zA-Z0-9._-]/g, "_")
      .replace(/_+/g, "_")
      .slice(-80);
    const id = (crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2)).slice(0, 8);
    return id + "-" + limpo;
  },

  async enviar(file, obraId, escopo) {
    if (!obraId) return { ok: false, erro: "Obra não identificada — recarregue a página." };
    const caminho = obraId + "/" + (escopo || "obra") + "/" + fileStore.nomeSeguro(file.name);
    const { error } = await supabase.storage
      .from(BUCKET_ANEXOS)
      .upload(caminho, file, { contentType: file.type || "application/octet-stream", upsert: false });
    if (error) {
      const m = String(error.message || "");
      if (/exceeded the maximum allowed size|payload too large/i.test(m))
        return { ok: false, erro: "Arquivo maior que o limite do servidor. Reduza o tamanho." };
      if (/new row violates row-level security|not authorized|denied/i.test(m))
        return { ok: false, erro: "Você não tem permissão para anexar arquivos nesta obra." };
      return { ok: false, erro: m };
    }
    return { ok: true, path: caminho };
  },

  // O bucket é privado: o acesso é por URL assinada, válida por 1 hora.
  async urlAssinada(caminho) {
    const { data, error } = await supabase.storage
      .from(BUCKET_ANEXOS).createSignedUrl(caminho, 3600);
    if (error || !data?.signedUrl)
      return { ok: false, erro: "Não foi possível abrir o arquivo: " + (error?.message || "sem URL") };
    return { ok: true, url: data.signedUrl };
  },

  async apagar(caminho) {
    if (!caminho) return { ok: true }; // arquivo antigo em base64: nada no Storage
    const { error } = await supabase.storage.from(BUCKET_ANEXOS).remove([caminho]);
    if (error) return { ok: false, erro: error.message };
    return { ok: true };
  }
};

function FileViewer({ file, onClose }) {
  const [url, setUrl] = useState(null);
  const [erroUrl, setErroUrl] = useState("");

  useEffect(() => {
    let vivo = true;
    setUrl(null); setErroUrl("");
    (async () => {
      if (!file) return;
      // Arquivo antigo (base64 no JSON, vindo de backup): a própria string é a fonte.
      if (file.data) { if (vivo) setUrl(file.data); return; }
      if (file.path) {
        const r = await fileStore.urlAssinada(file.path);
        if (!vivo) return;
        if (r.ok) setUrl(r.url); else setErroUrl(r.erro);
      } else if (vivo) {
        setErroUrl("Arquivo sem origem registrada.");
      }
    })();
    return () => { vivo = false; };
  }, [file]);

  if (!file) return null;
  const group = fileTypeGroup(file.tipo);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.88)", zIndex: 500,
      display: "flex", flexDirection: "column", padding: 16
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, color: "white" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileIcon(file.tipo)} {file.nome}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontFamily: "'Montserrat',sans-serif", marginTop: 2 }}>{fBytes(file.tamanho)} · {file.tipo}</div>
        </div>
        <a href={url || "#"} download={file.nome} onClick={e => { e.stopPropagation(); if (!url) e.preventDefault(); }} style={{ padding: "7px 12px", borderRadius: 8, background: B.dourado, color: B.cafe, fontSize: 11, fontWeight: 700, textDecoration: "none", fontFamily: "'Montserrat',sans-serif" }}>⬇ Baixar</a>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", color: "white", width: 36, height: 36, borderRadius: 8, cursor: "pointer", fontSize: 18 }}>✕</button>
      </div>
      <div onClick={e => e.stopPropagation()} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto", minHeight: 0 }}>
        {!url && !erroUrl && <div style={{ color: "rgba(255,255,255,.7)", fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>⏳ Carregando arquivo...</div>}
        {erroUrl && <div style={{ color: "#ffb4a8", fontFamily: "'Montserrat',sans-serif", fontSize: 13, textAlign: "center", padding: 24 }}>⚠ {erroUrl}</div>}
        {url && group === "image" && <img src={url} alt={file.nome} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }} />}
        {url && group === "video" && <video src={url} controls style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8 }} />}
        {url && group === "pdf" && <embed src={url} type="application/pdf" style={{ width: "100%", height: "100%", minHeight: 400, borderRadius: 8 }} />}
        {url && group === "other" && (
          <div style={{ textAlign: "center", color: "white", fontFamily: "'Montserrat',sans-serif", padding: 40 }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>📎</div>
            <div style={{ fontSize: 14, marginBottom: 14 }}>Pré-visualização não disponível</div>
            <a href={url} download={file.nome} style={{ padding: "10px 18px", background: B.dourado, color: B.cafe, borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>⬇ Baixar arquivo</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FILE MANAGER (upload + list + view + delete) ────────────────────────────
function FileManager({ arquivos = [], onChange, user, readOnly, compact, obraId, escopo }) {
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(0);

  const handleFiles = async (files) => {
    setError("");
    const list = Array.from(files);
    const newItems = [];
    setEnviando(list.length);
    for (const f of list) {
      if (f.size > MAX_FILE_SIZE) {
        setError(`"${f.name}" excede o limite de 50 MB.`);
        setEnviando(n => n - 1);
        continue;
      }
      // Vai para o Storage, não mais para dentro do JSON da obra. O registro
      // guarda só o caminho — leve, e sem o teto de armazenamento do navegador.
      const r = await fileStore.enviar(f, obraId, escopo);
      setEnviando(n => n - 1);
      if (!r.ok) { setError(`"${f.name}": ${r.erro}`); continue; }
      newItems.push({
        id: Date.now() + Math.random(),
        nome: f.name, tipo: f.type, tamanho: f.size,
        path: r.path, uploadedAt: new Date().toISOString(),
        uploadedBy: user?.email || "desconhecido"
      });
    }
    if (newItems.length) onChange([...arquivos, ...newItems]);
  };

  const remove = async (id) => {
    const alvo = arquivos.find(a => a.id === id);
    if (!window.confirm("Remover este arquivo? Esta ação não pode ser desfeita.")) return;
    // Apaga do Storage também, senão o arquivo ficaria órfão ocupando espaço.
    if (alvo?.path) {
      const r = await fileStore.apagar(alvo.path);
      if (!r.ok) { setError("Não foi possível apagar no servidor: " + r.erro); return; }
    }
    onChange(arquivos.filter(a => a.id !== id));
  };

  return (
    <div>
      {!readOnly && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", cursor: "pointer", border: "2px dashed " + B.creme, borderRadius: 10, padding: compact ? "12px" : "20px", textAlign: "center", background: B.offwhite }}>
            <div style={{ fontSize: compact ? 18 : 24, marginBottom: 4 }}>📤</div>
            <div style={{ fontSize: compact ? 11 : 13, fontWeight: 700, color: B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>Adicionar arquivo</div>
            <div style={{ fontSize: 10, color: B.cinza, marginTop: 3, fontFamily: "'Montserrat',sans-serif" }}>PDF, imagem ou vídeo · máx 50 MB</div>
            <input type="file" multiple accept="image/*,video/*,application/pdf" onChange={e => handleFiles(e.target.files)} style={{ display: "none" }} />
          </label>
          {enviando > 0 && <div style={{ background: "#fff8e6", color: B.caramelo, padding: "8px 12px", borderRadius: 6, fontSize: 11, marginTop: 8, fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>⏳ Enviando {enviando} arquivo(s)...</div>}
          {error && <div style={{ background: "#fdecea", color: B.vermelho, padding: "8px 12px", borderRadius: 6, fontSize: 11, marginTop: 8, fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>⚠ {error}</div>}
        </div>
      )}

      {arquivos.length === 0
        ? <div style={{ textAlign: "center", padding: 18, color: B.cinza, fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>Nenhum arquivo.</div>
        : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {arquivos.map(a => (
              <div key={a.id} style={{ background: B.branco, border: "1px solid " + B.creme, borderRadius: 8, padding: "9px 11px", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ fontSize: 20 }}>{fileIcon(a.tipo)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: B.cafe, fontFamily: "'Montserrat',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.nome}</div>
                  <div style={{ fontSize: 9, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{fBytes(a.tamanho)} · {new Date(a.uploadedAt).toLocaleDateString("pt-BR")}</div>
                </div>
                <button onClick={() => setViewing(a)} style={{ background: B.offwhite, border: "1px solid " + B.creme, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: B.caramelo, fontFamily: "'Montserrat',sans-serif", minHeight: 32 }}>👁</button>
                {!readOnly && <button onClick={() => remove(a.id)} style={{ background: "#fdecea", border: "none", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: B.vermelho, minHeight: 32 }}>✕</button>}
              </div>
            ))}
          </div>
      }
      {viewing && <FileViewer file={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

// ─── SVG CHARTS ──────────────────────────────────────────────────────────────
function LineChartSVG({ data, dataKey, color }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d[dataKey]);
  const max = Math.max(...vals, 1);
  const W = 300, H = 120, PL = 30, PB = 20, PW = W - PL - 10, PH = H - PB - 10;
  const pts = vals.map((v, i) => {
    const x = PL + (i / (vals.length - 1)) * PW;
    const y = 10 + (1 - v / max) * PH;
    return x + "," + y;
  }).join(" ");
  return (
    <svg width="100%" viewBox={"0 0 " + W + " " + H} style={{ overflow: "visible" }}>
      <line x1={PL} y1={10} x2={PL} y2={10 + PH} stroke={B.creme} strokeWidth={1} />
      <line x1={PL} y1={10 + PH} x2={PL + PW} y2={10 + PH} stroke={B.creme} strokeWidth={1} />
      {[0, 25, 50, 75, 100].map(v => {
        const y = 10 + (1 - v / 100) * PH;
        return <g key={v}><line x1={PL - 4} y1={y} x2={PL + PW} y2={y} stroke={B.creme} strokeWidth={0.5} /><text x={PL - 6} y={y + 4} textAnchor="end" fontSize={8} fill={B.cinza}>{v}</text></g>;
      })}
      {data.map((d, i) => {
        const x = PL + (i / (vals.length - 1)) * PW;
        return <text key={i} x={x} y={H - 4} textAnchor="middle" fontSize={8} fill={B.cinza}>{d.semana}</text>;
      })}
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" />
      {vals.map((v, i) => {
        const x = PL + (i / (vals.length - 1)) * PW;
        const y = 10 + (1 - v / max) * PH;
        return <circle key={i} cx={x} cy={y} r={3} fill={color} />;
      })}
    </svg>
  );
}

function BarChartSVG({ data }) {
  if (!data || !data.length) return null;
  const max = Math.max(...data.map(d => d.avanco), 1);
  const W = 300, H = 130, PL = 10, PB = 24, PW = W - PL - 10, PH = H - PB - 10;
  const bw = PW / data.length;
  return (
    <svg width="100%" viewBox={"0 0 " + W + " " + H} style={{ overflow: "visible" }}>
      {data.map((d, i) => {
        const bh = (d.avanco / max) * PH;
        const x = PL + i * bw + bw * 0.1;
        const barW = bw * 0.8;
        const y = 10 + PH - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} fill={B.caramelo} rx={3} />
            <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize={9} fill={B.cafe} fontWeight="bold">{d.avanco}%</text>
            <text x={x + barW / 2} y={H - 6} textAnchor="middle" fontSize={8} fill={B.cinza}>{d.name}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── GANTT ────────────────────────────────────────────────────────────────────
function Gantt({ tarefas, elap, baseline, filterP, filterS, svgRef }) {
  const rows = tarefas.filter(tk => {
    if (filterP !== "Todos" && String(tk.p) !== filterP) return false;
    if (filterS !== "Todos" && tk.status !== filterS) return false;
    return true;
  });
  const ROW = 34, LW = 185, DW = 9, MAX = 95, HH = 38, CW = MAX * DW;
  const gc = (s) => ({ "Concluído": B.verde, "Em Andamento": B.latte, "Atrasado": B.vermelho, "Bloqueado": B.cinza }[s] || B.caramelo);
  const weeks = Array.from({ length: Math.ceil(MAX / 7) }, (_, i) => i);
  return (
    <div style={{ overflowX: "auto", border: "1px solid " + B.creme, borderRadius: 10, background: B.branco, WebkitOverflowScrolling: "touch" }}>
      <svg ref={svgRef} width={LW + CW + 16} height={HH + rows.length * ROW + 10} style={{ display: "block", fontFamily: "'Montserrat',sans-serif" }}>
        <rect x={0} y={0} width={LW} height={HH} fill={B.cafe} />
        <text x={10} y={25} fill={B.creme} fontSize={10} fontWeight="bold">TAREFA</text>
        {weeks.map(w => (
          <g key={w}>
            <rect x={LW + w * 7 * DW} y={0} width={7 * DW} height={HH} fill={w % 2 === 0 ? B.cafe : B.marrom} />
            <text x={LW + w * 7 * DW + 4} y={16} fill={B.dourado} fontSize={9} fontWeight="bold">S{w + 1}</text>
            <text x={LW + w * 7 * DW + 4} y={30} fill={B.creme + "88"} fontSize={8}>D{w * 7}</text>
          </g>
        ))}
        {rows.map((tk, i) => {
          const y = HH + i * ROW, bx = LW + tk.s * DW, bw = Math.max(4, tk.d * DW);
          const fw = bw * (tk.progresso / 100), c = gc(tk.status);
          const blocked = isBlocked(tk, tarefas), dl = daysLeft(tk, elap);
          return (
            <g key={tk.id}>
              <rect x={0} y={y} width={LW + CW + 16} height={ROW} fill={i % 2 === 0 ? B.offwhite : B.branco} />
              <rect x={2} y={y + 11} width={3} height={ROW - 22} rx={1} fill={prioColor(tk.p)} />
              <text x={13} y={y + ROW / 2 + 4} fontSize={10} fill={tk.status === "Atrasado" ? B.vermelho : B.cafe} fontWeight={tk.status === "Atrasado" ? "bold" : "normal"}>
                {tk.t.length > 22 ? tk.t.slice(0, 22) + "…" : tk.t}
              </text>
              {weeks.map(w => <line key={w} x1={LW + w * 7 * DW} y1={y} x2={LW + w * 7 * DW} y2={y + ROW} stroke={B.creme} strokeWidth={0.5} />)}
              {baseline && <rect x={bx} y={y + 10} width={bw} height={ROW - 20} rx={2} fill="none" stroke={B.cinza} strokeWidth={1} strokeDasharray="3,2" opacity={0.5} />}
              <rect x={bx} y={y + 8} width={bw} height={ROW - 16} rx={3} fill={c + "28"} stroke={c} strokeWidth={0.5} />
              {fw > 0 && <rect x={bx} y={y + 8} width={fw} height={ROW - 16} rx={3} fill={c} opacity={0.85} />}
              {bw > 28 && <text x={bx + bw / 2} y={y + ROW / 2 + 4} textAnchor="middle" fontSize={9} fill={tk.progresso > 55 ? "white" : B.cafe} fontWeight="bold">{tk.progresso}%</text>}
              {tk.status === "Atrasado" && <text x={bx + bw + 4} y={y + ROW / 2 + 4} fontSize={12} fill={B.vermelho}>⚠</text>}
              {blocked && <text x={bx + bw + 4} y={y + ROW / 2 + 4} fontSize={12}>🔒</text>}
              {dl <= 3 && dl >= 0 && tk.status !== "Concluído" && <text x={bx + bw + 4} y={y + ROW / 2 + 4} fontSize={10} fill={B.laranja}>⏰</text>}
              <line x1={0} y1={y + ROW} x2={LW + CW + 16} y2={y + ROW} stroke={B.creme} strokeWidth={0.5} />
            </g>
          );
        })}
        {elap > 0 && elap < MAX && (
          <>
            <line x1={LW + elap * DW} y1={0} x2={LW + elap * DW} y2={HH + rows.length * ROW} stroke={B.vermelho} strokeWidth={2} strokeDasharray="4,3" />
            <rect x={LW + elap * DW - 16} y={HH - 2} width={32} height={13} rx={3} fill={B.vermelho} />
            <text x={LW + elap * DW} y={HH + 7} textAnchor="middle" fontSize={8} fill="white" fontWeight="bold">HOJE</text>
          </>
        )}
      </svg>
    </div>
  );
}

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────
function LandingScreen({ onAccess, onVisitor }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(145deg, #1a0e08 0%, #2c1810 40%, #3d2214 70%, #4a3020 100%)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: isMobile ? "32px 24px" : "48px 32px", position: "relative", overflow: "hidden"
    }}>
      {/* Decorative circles */}
      <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", border: "1px solid rgba(196,164,120,.08)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", border: "1px solid rgba(196,164,120,.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -100, left: -100, width: 400, height: 400, borderRadius: "50%", border: "1px solid rgba(196,164,120,.05)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: 60, left: 60, width: 200, height: 200, borderRadius: "50%", border: "1px solid rgba(196,164,120,.04)", pointerEvents: "none" }} />

      {/* Content */}
      <div style={{ textAlign: "center", zIndex: 1, maxWidth: 480, width: "100%" }}>
        {/* Logo mark */}
        <div style={{ marginBottom: 28 }}>
          <div style={{
            width: 72, height: 72, borderRadius: 20, margin: "0 auto 16px",
            background: "linear-gradient(135deg, rgba(196,164,120,.2) 0%, rgba(196,164,120,.08) 100%)",
            border: "1px solid rgba(196,164,120,.3)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <rect x="4" y="14" width="28" height="3" rx="1.5" fill="#c4a478" opacity="0.9"/>
              <rect x="4" y="20" width="20" height="3" rx="1.5" fill="#c4a478" opacity="0.6"/>
              <rect x="4" y="26" width="24" height="3" rx="1.5" fill="#c4a478" opacity="0.4"/>
              <circle cx="27" cy="10" r="5" stroke="#c4a478" strokeWidth="2" fill="none" opacity="0.8"/>
              <line x1="27" y1="4" x2="27" y2="6" stroke="#c4a478" strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
            </svg>
          </div>
          <div style={{ fontSize: 9, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(196,164,120,.5)", marginBottom: 8, fontWeight: 600 }}>
            Grupo Alpha
          </div>
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: "'Montserrat',sans-serif", fontWeight: 900,
          fontSize: isMobile ? 32 : 42, color: "#f5ebe0", margin: "0 0 10px",
          letterSpacing: "-0.5px", lineHeight: 1.15
        }}>
          Alpha<br />
          <span style={{ color: "#c4a478" }}>Implantações</span>
        </h1>

        {/* Tagline */}
        <p style={{
          fontSize: isMobile ? 13 : 15, color: "rgba(245,235,224,.5)",
          margin: "0 0 40px", lineHeight: 1.65, fontWeight: 400,
          fontFamily: "'Montserrat',sans-serif"
        }}>
          Gestão inteligente de implantações<br />
          <span style={{ color: "rgba(196,164,120,.7)", fontWeight: 600 }}>Ponto Alpha Café</span>
        </p>

        {/* Stats row */}
        <div style={{ display: "flex", justifyContent: "center", gap: 28, marginBottom: 44 }}>
          {[["37", "Tarefas"], ["3", "Obras Ativas"], ["IA", "Integrada"]].map(([v, l]) => (
            <div key={l} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 20, color: "#c4a478" }}>{v}</div>
              <div style={{ fontSize: 9, color: "rgba(245,235,224,.35)", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <button
          onClick={onAccess}
          style={{
            width: "100%", maxWidth: 320, padding: "16px 32px",
            background: "linear-gradient(135deg, #c4a478 0%, #b8935e 100%)",
            border: "none", borderRadius: 12, cursor: "pointer",
            fontFamily: "'Montserrat',sans-serif", fontWeight: 800,
            fontSize: 15, color: "#2c1810", letterSpacing: "0.3px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            margin: "0 auto", transition: "opacity .2s"
          }}
          onMouseOver={e => e.currentTarget.style.opacity = "0.92"}
          onMouseOut={e => e.currentTarget.style.opacity = "1"}
        >
          Acessar o Sistema
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="#2c1810" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Visitor Button */}
        <button
          onClick={onVisitor}
          style={{
            width: "100%", maxWidth: 320, padding: "13px 32px", marginTop: 12,
            background: "transparent", border: "1px solid rgba(196,164,120,.3)",
            borderRadius: 12, cursor: "pointer",
            fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
            fontSize: 13, color: "rgba(196,164,120,.9)", letterSpacing: "0.3px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            margin: "12px auto 0", transition: "background .2s"
          }}
          onMouseOver={e => e.currentTarget.style.background = "rgba(196,164,120,.08)"}
          onMouseOut={e => e.currentTarget.style.background = "transparent"}
        >
          👁 Visualizar como visitante
        </button>

        {/* Footer */}
        <div style={{ marginTop: 38, fontSize: 10, color: "rgba(245,235,224,.2)", fontFamily: "'Montserrat',sans-serif", letterSpacing: "1px", textTransform: "uppercase" }}>
          v2.7 · Ponto Alpha Café
        </div>
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, onSignup, onForgot }) {
  const [email, setEmail] = useState(sessionStore.loadEmail());
  const [senha, setSenha] = useState("");
  const [remember, setRemember] = useState(!!sessionStore.loadEmail());
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handleLogin = async () => {
    if (!email.trim() || !senha.trim()) { setErro("Preencha email e senha."); return; }
    setLoading(true);
    setErro("");
    const res = await userStore.authenticate(email, senha);
    if (res.ok) {
      if (remember) sessionStore.saveEmail(email);
      else sessionStore.clear();
      onLogin(res.user);
    } else {
      setErro(res.erro);
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(145deg, #1a0e08 0%, #2c1810 40%, #3d2214 70%, #4a3020 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px", position: "relative", overflow: "hidden"
    }}>
      <div style={{ position: "absolute", top: -60, right: -60, width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(196,164,120,.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 340, height: 340, borderRadius: "50%", border: "1px solid rgba(196,164,120,.05)", pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 400, zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, margin: "0 auto 14px",
            background: "rgba(196,164,120,.12)", border: "1px solid rgba(196,164,120,.25)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <svg width="24" height="24" viewBox="0 0 36 36" fill="none">
              <rect x="4" y="14" width="28" height="3" rx="1.5" fill="#c4a478" opacity="0.9"/>
              <rect x="4" y="20" width="20" height="3" rx="1.5" fill="#c4a478" opacity="0.6"/>
              <circle cx="27" cy="10" r="5" stroke="#c4a478" strokeWidth="2" fill="none" opacity="0.8"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 22, color: "#f5ebe0", margin: "0 0 5px" }}>
            Bem-vindo
          </h2>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "rgba(245,235,224,.4)", margin: 0 }}>
            Alpha Implantações
          </p>
        </div>

        {/* Card */}
        <div style={{ background: "rgba(250,246,241,.05)", border: "1px solid rgba(196,164,120,.18)", borderRadius: 18, padding: isMobile ? "24px 20px" : "32px 28px", backdropFilter: "blur(8px)" }}>
          {/* Email */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Email</label>
            <input
              type="email" value={email} onChange={e => { setEmail(e.target.value); setErro(""); }}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              placeholder="seu@email.com"
              autoComplete="username"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(196,164,120,.22)", background: "rgba(250,246,241,.06)", fontSize: 13, color: "#f5ebe0", fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box", outline: "none", transition: "border .2s" }}
            />
          </div>

          {/* Senha */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Senha</label>
            <PasswordInput value={senha} onChange={v => { setSenha(v); setErro(""); }} onEnter={handleLogin} placeholder="••••••••" dark />
          </div>

          {/* Lembrar acesso */}
          <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", padding: "6px 0", marginBottom: 6 }}>
            <input
              type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
              style={{ width: 17, height: 17, cursor: "pointer", accentColor: "#c4a478" }}
            />
            <span style={{ fontSize: 12, color: "rgba(245,235,224,.65)", fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>
              Lembrar meu acesso
            </span>
          </label>

          {/* Erro */}
          {erro && (
            <div style={{ background: "rgba(176,48,32,.2)", border: "1px solid rgba(176,48,32,.4)", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#ff9a8a", fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>
              ⚠ {erro}
            </div>
          )}

          {/* Botão */}
          <button
            onClick={handleLogin}
            disabled={loading}
            style={{
              width: "100%", padding: "14px", marginTop: 16,
              background: loading ? "rgba(196,164,120,.5)" : "linear-gradient(135deg, #c4a478 0%, #b8935e 100%)",
              border: "none", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 14,
              color: "#2c1810", letterSpacing: "0.3px", transition: "opacity .2s"
            }}
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>

          {/* Esqueci minha senha */}
          <div style={{ textAlign: "right", marginTop: 8 }}>
            <button
              onClick={onForgot}
              style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600, color: "rgba(196,164,120,.6)", padding: 0 }}
            >
              Esqueci minha senha
            </button>
          </div>

          {/* Criar conta */}
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "rgba(245,235,224,.4)" }}>Não tem conta?{" "}</span>
            <button
              onClick={onSignup}
              style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: "#c4a478", padding: 0 }}
            >
              Criar conta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SIGNUP SCREEN ────────────────────────────────────────────────────────────
function SignupScreen({ onBack, onCadastrar }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState("");
  const [success, setSuccess] = useState(false);
  const isMobile = useIsMobile();

  const handleCadastro = async () => {
    setErro("");
    if (!nome.trim() || !email.trim() || !senha.trim() || !confirma.trim()) {
      setErro("Preencha todos os campos."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErro("Digite um email válido."); return;
    }
    if (senha.trim().length < 6) {
      setErro("A senha deve ter pelo menos 6 caracteres."); return;
    }
    if (senha.trim() !== confirma.trim()) {
      setErro("As senhas não coincidem."); return;
    }
    const result = await userStore.add(nome, email, senha);
    if (!result.ok) { setErro(result.erro); return; }
    setSuccess(true);
    setTimeout(() => { onCadastrar && onCadastrar(); onBack(); }, 2000);
  };

  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(145deg, #1a0e08 0%, #2c1810 40%, #3d2214 70%, #4a3020 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px", position: "relative", overflow: "hidden"
    }}>
      <div style={{ position: "absolute", top: -60, left: -60, width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(196,164,120,.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, right: -80, width: 340, height: 340, borderRadius: "50%", border: "1px solid rgba(196,164,120,.05)", pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 400, zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, margin: "0 auto 14px",
            background: "rgba(196,164,120,.12)", border: "1px solid rgba(196,164,120,.25)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4" stroke="#c4a478" strokeWidth="2"/>
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#c4a478" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 22, color: "#f5ebe0", margin: "0 0 5px" }}>
            Criar conta
          </h2>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "rgba(245,235,224,.4)", margin: 0 }}>
            Alpha Implantações
          </p>
        </div>

        {/* Card */}
        <div style={{ background: "rgba(250,246,241,.05)", border: "1px solid rgba(196,164,120,.18)", borderRadius: 18, padding: isMobile ? "24px 20px" : "32px 28px" }}>
          {success ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 16, color: "#f5ebe0", marginBottom: 8 }}>Cadastro enviado!</div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "rgba(245,235,224,.5)", lineHeight: 1.6, padding: "0 8px" }}>
                Seu acesso precisa ser aprovado por um administrador. Você será notificado quando for liberado.
              </div>
            </div>
          ) : (
            <>
              {/* Nome */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Nome Completo</label>
                <input
                  type="text" value={nome} onChange={e => { setNome(e.target.value); setErro(""); }}
                  placeholder="Seu nome completo"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(196,164,120,.22)", background: "rgba(250,246,241,.06)", fontSize: 13, color: "#f5ebe0", fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box", outline: "none" }}
                />
              </div>

              {/* Email */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Email</label>
                <input
                  type="email" value={email} onChange={e => { setEmail(e.target.value); setErro(""); }}
                  placeholder="seu@email.com"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(196,164,120,.22)", background: "rgba(250,246,241,.06)", fontSize: 13, color: "#f5ebe0", fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box", outline: "none" }}
                />
              </div>

              {/* Senha */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Senha <span style={{ fontWeight: 400, opacity: .6 }}>(mín. 6 caracteres)</span></label>
                <PasswordInput value={senha} onChange={v => { setSenha(v); setErro(""); }} placeholder="••••••••" dark />
              </div>

              {/* Confirmar Senha */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Confirmar Senha</label>
                <PasswordInput value={confirma} onChange={v => { setConfirma(v); setErro(""); }} onEnter={handleCadastro} placeholder="Repita a senha" dark />
                {confirma && senha && confirma !== senha && (
                  <div style={{ fontSize: 10, color: "#ff9a8a", marginTop: 4, fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>As senhas não coincidem</div>
                )}
              </div>

              {/* Erro */}
              {erro && (
                <div style={{ background: "rgba(176,48,32,.2)", border: "1px solid rgba(176,48,32,.4)", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#ff9a8a", fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>
                  ⚠ {erro}
                </div>
              )}

              <button
                onClick={handleCadastro}
                style={{
                  width: "100%", padding: "14px",
                  background: "linear-gradient(135deg, #c4a478 0%, #b8935e 100%)",
                  border: "none", borderRadius: 10, cursor: "pointer",
                  fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 14,
                  color: "#2c1810", letterSpacing: "0.3px"
                }}
              >
                Criar Conta
              </button>

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button
                  onClick={onBack}
                  style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: "rgba(196,164,120,.7)", padding: 0 }}
                >
                  ← Voltar ao login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── FORGOT PASSWORD SCREEN ───────────────────────────────────────────────────
function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("form"); // form | sent
  const [erro, setErro] = useState("");
  const isMobile = useIsMobile();

  const [enviando, setEnviando] = useState(false);

  // Agora envia email de verdade, pelo Supabase. O link volta para o próprio
  // sistema, onde a pessoa define a nova senha — sem depender de admin nenhum.
  const handleEnviar = async () => {
    const emailNorm = email.trim().toLowerCase();
    if (!emailNorm) { setErro("Digite seu email para continuar."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailNorm)) { setErro("Email inválido."); return; }
    setErro("");
    setEnviando(true);
    const res = await userStore.enviarRedefinicao(emailNorm);
    setEnviando(false);
    if (!res.ok) { setErro(res.erro); return; }
    setStep("sent");
  };

  const openMailto = () => {
    const subject = encodeURIComponent("Ajuda com acesso — Alpha Implantações");
    const body = encodeURIComponent(
      "Olá administradores,\n\n" +
      "Não estou conseguindo redefinir a senha da minha conta no Alpha Implantações.\n\n" +
      "Email cadastrado: " + email.trim().toLowerCase() + "\n" +
      "Data: " + new Date().toLocaleString("pt-BR") + "\n\n" +
      "Obrigado(a)."
    );
    window.location.href = "mailto:?subject=" + subject + "&body=" + body;
  };

  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(145deg, #1a0e08 0%, #2c1810 40%, #3d2214 70%, #4a3020 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px", position: "relative", overflow: "hidden"
    }}>
      <div style={{ position: "absolute", top: -60, right: -60, width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(196,164,120,.06)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 340, height: 340, borderRadius: "50%", border: "1px solid rgba(196,164,120,.05)", pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 400, zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, margin: "0 auto 14px",
            background: "rgba(196,164,120,.12)", border: "1px solid rgba(196,164,120,.25)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke="#c4a478" strokeWidth="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#c4a478" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1.5" fill="#c4a478"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 22, color: "#f5ebe0", margin: "0 0 5px" }}>
            Recuperar Senha
          </h2>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "rgba(245,235,224,.4)", margin: 0 }}>
            Alpha Implantações
          </p>
        </div>

        {/* Card */}
        <div style={{ background: "rgba(250,246,241,.05)", border: "1px solid rgba(196,164,120,.18)", borderRadius: 18, padding: isMobile ? "24px 20px" : "32px 28px" }}>
          {step === "form" ? (
            <>
              <div style={{ fontSize: 13, color: "rgba(245,235,224,.55)", fontFamily: "'Montserrat',sans-serif", lineHeight: 1.65, marginBottom: 22 }}>
                Digite o email cadastrado na sua conta. Se ele existir em nosso sistema, você receberá um link de recuperação em instantes.
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Email cadastrado</label>
                <input
                  type="email" value={email}
                  onChange={e => { setEmail(e.target.value); setErro(""); }}
                  onKeyDown={e => e.key === "Enter" && handleEnviar()}
                  placeholder="seu@email.com"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid " + (erro ? "rgba(176,48,32,.5)" : "rgba(196,164,120,.22)"), background: "rgba(250,246,241,.06)", fontSize: 13, color: "#f5ebe0", fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box", outline: "none" }}
                />
              </div>

              {erro && (
                <div style={{ background: "rgba(176,48,32,.2)", border: "1px solid rgba(176,48,32,.4)", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#ff9a8a", fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>
                  ⚠ {erro}
                </div>
              )}

              <button
                onClick={handleEnviar}
                style={{
                  width: "100%", padding: "14px", marginTop: 8,
                  background: "linear-gradient(135deg, #c4a478 0%, #b8935e 100%)",
                  border: "none", borderRadius: 10, cursor: "pointer",
                  fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 14,
                  color: "#2c1810", letterSpacing: "0.3px"
                }}
              >
                Enviar link de recuperação
              </button>

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: "rgba(196,164,120,.7)", padding: 0 }}>
                  ← Voltar ao login
                </button>
              </div>
            </>
          ) : (
            /* ── Sent state ── */
            <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%", margin: "0 auto 18px",
                background: "rgba(107,142,90,.15)", border: "1px solid rgba(107,142,90,.3)",
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" stroke="#6b8e5a" strokeWidth="1.8"/>
                  <path d="M22 6l-10 7L2 6" stroke="#6b8e5a" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </div>

              <div style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 17, color: "#f5ebe0", marginBottom: 10 }}>
                Solicitação registrada!
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "rgba(245,235,224,.55)", lineHeight: 1.7, marginBottom: 16, padding: "0 4px" }}>
                Sua solicitação para
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: "#c4a478", marginBottom: 16, wordBreak: "break-all" }}>
                {email.trim().toLowerCase()}
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "rgba(245,235,224,.5)", lineHeight: 1.6, marginBottom: 20 }}>
                foi enviada aos administradores. Eles vão gerar uma senha temporária e te enviar.
              </div>

              <div style={{ background: "rgba(196,164,120,.08)", border: "1px solid rgba(196,164,120,.18)", borderRadius: 10, padding: "14px", marginBottom: 18, textAlign: "left" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.9)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8, fontFamily: "'Montserrat',sans-serif" }}>Para acelerar o processo</div>
                <div style={{ fontSize: 11, color: "rgba(245,235,224,.55)", fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6, marginBottom: 10 }}>
                  Envie um email diretamente aos administradores com a sua solicitação:
                </div>
                <button
                  onClick={openMailto}
                  style={{
                    width: "100%", padding: "11px", background: "rgba(196,164,120,.18)",
                    border: "1px solid rgba(196,164,120,.4)", borderRadius: 8, cursor: "pointer",
                    fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 12, color: "#c4a478",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6
                  }}
                >
                  ✉️ Enviar email aos administradores
                </button>
              </div>

              <button
                onClick={onBack}
                style={{
                  width: "100%", padding: "13px",
                  background: "linear-gradient(135deg, #c4a478 0%, #b8935e 100%)",
                  border: "none", borderRadius: 10, cursor: "pointer",
                  fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 14,
                  color: "#2c1810"
                }}
              >
                Voltar ao login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CHANGE PASSWORD (forçado em primeiro login após reset) ──────────────────
function ChangePasswordScreen({ user, onDone, onLogout }) {
  const [novaSenha, setNovaSenha] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState("");
  const isMobile = useIsMobile();

  const [salvando, setSalvando] = useState(false);

  const handleSave = async () => {
    setErro("");
    if (!novaSenha || !confirma) { setErro("Preencha os dois campos."); return; }
    if (novaSenha.length < 6) { setErro("A senha deve ter pelo menos 6 caracteres."); return; }
    if (novaSenha !== confirma) { setErro("As senhas não coincidem."); return; }
    setSalvando(true);
    // A senha nunca passa por aqui em texto: o Supabase recebe e guarda o hash.
    const res = await userStore.trocarSenha(novaSenha);
    setSalvando(false);
    if (!res.ok) { setErro(res.erro); return; }
    onDone({ ...user, mustChangePassword: false });
  };

  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(145deg, #1a0e08 0%, #2c1810 40%, #3d2214 70%, #4a3020 100%)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "24px"
    }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, margin: "0 auto 12px", background: "rgba(212,120,58,.15)", border: "1px solid rgba(212,120,58,.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>🔑</div>
          <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 20, color: "#f5ebe0", margin: "0 0 6px" }}>Trocar senha</h2>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "rgba(245,235,224,.5)", margin: 0, lineHeight: 1.6 }}>
            Por segurança, você precisa definir uma nova senha pessoal antes de continuar.
          </p>
        </div>

        <div style={{ background: "rgba(250,246,241,.05)", border: "1px solid rgba(196,164,120,.18)", borderRadius: 18, padding: isMobile ? "22px 18px" : "28px 26px" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Nova Senha <span style={{ fontWeight: 400, opacity: .6 }}>(mín. 6 caracteres)</span></label>
            <PasswordInput value={novaSenha} onChange={v => { setNovaSenha(v); setErro(""); }} placeholder="••••••••" dark />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Confirmar Nova Senha</label>
            <PasswordInput value={confirma} onChange={v => { setConfirma(v); setErro(""); }} onEnter={handleSave} placeholder="Repita a nova senha" dark />
          </div>

          {erro && (
            <div style={{ background: "rgba(176,48,32,.2)", border: "1px solid rgba(176,48,32,.4)", borderRadius: 8, padding: "9px 12px", marginBottom: 14, fontSize: 12, color: "#ff9a8a", fontFamily: "'Montserrat',sans-serif", fontWeight: 600 }}>⚠ {erro}</div>
          )}

          <button
            onClick={handleSave}
            style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg, #c4a478 0%, #b8935e 100%)", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 14, color: "#2c1810" }}
          >
            Salvar nova senha
          </button>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={onLogout} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600, color: "rgba(196,164,120,.6)", padding: 0 }}>
              Sair
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PENDING APPROVAL SCREEN ─────────────────────────────────────────────────
function PendingApprovalScreen({ user, onLogout }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(145deg, #1a0e08 0%, #2c1810 40%, #3d2214 70%, #4a3020 100%)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "24px"
    }}>
      <div style={{ width: "100%", maxWidth: 420, textAlign: "center" }}>
        <div style={{
          width: 68, height: 68, borderRadius: 18, margin: "0 auto 18px",
          background: "rgba(212,120,58,.15)", border: "1px solid rgba(212,120,58,.3)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30
        }}>⏳</div>
        <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 22, color: "#f5ebe0", margin: "0 0 10px" }}>
          Aguardando Aprovação
        </h2>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "rgba(245,235,224,.55)", lineHeight: 1.7, margin: "0 0 28px", padding: "0 12px" }}>
          Olá, <strong style={{ color: "#c4a478" }}>{user.nome}</strong>! Sua conta foi criada com sucesso, mas ainda precisa ser aprovada por um administrador antes de acessar o sistema.
        </p>
        <div style={{ background: "rgba(250,246,241,.05)", border: "1px solid rgba(196,164,120,.18)", borderRadius: 12, padding: isMobile ? "18px" : "22px", marginBottom: 24, textAlign: "left" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(196,164,120,.8)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>Administradores do sistema</div>
          {ADMIN_CONTATOS.map(a => (
            <div key={a.email} style={{ fontSize: 12, color: "rgba(245,235,224,.7)", fontFamily: "'Montserrat',sans-serif", marginBottom: 6 }}>
              👤 <strong style={{ color: "#c4a478" }}>{a.nome}</strong> · {a.email}
            </div>
          ))}
          <div style={{ marginTop: 12, fontSize: 11, color: "rgba(245,235,224,.4)", fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6 }}>
            Entre em contato por email ou WhatsApp para solicitar liberação do acesso.
          </div>
        </div>
        <button onClick={onLogout} style={{
          padding: "12px 28px", borderRadius: 10, border: "1px solid rgba(196,164,120,.3)",
          background: "rgba(196,164,120,.08)", color: "#c4a478", cursor: "pointer",
          fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 13
        }}>← Voltar ao login</button>
      </div>
    </div>
  );
}

// ─── USUÁRIOS (ADMIN) ─────────────────────────────────────────────────────────
function UsuariosView({ currentUser, obras, isMobile, refreshKey, onRefresh }) {
  const [tab, setTab] = useState("pendentes");
  const [users, setUsers] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null); // { user, newPassword }
  const [creatingUser, setCreatingUser] = useState({ nome: "", email: "", senha: "" });
  const [userMsg, setUserMsg] = useState("");
  const [novoAdm, setNovoAdm] = useState({ nome: "", email: "", senha: "" });
  const [admMsg, setAdmMsg] = useState("");

  const [carregando, setCarregando] = useState(true);

  const carregar = async () => {
    setCarregando(true);
    const [us, lg] = await Promise.all([userStore.getAll(), adminLogStore.getAll()]);
    setUsers(us);
    setLogs(lg);
    setCarregando(false);
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [refreshKey]);

  const reload = async () => { await carregar(); onRefresh && onRefresh(); };

  const logAction = (action, target, detail = "") =>
    adminLogStore.add(currentUser.email, action, target, detail);

  // Os handlers recebem email (como o JSX já chamava) e resolvem o id internamente,
  // porque no banco a chave é o id do usuário, não o email.
  const idDe = (email) => users.find(x => x.email === email)?.id;

  const aplicar = async (email, updates, acao, detalhe = "") => {
    const id = idDe(email);
    if (!id) { alert("Usuário não encontrado. Recarregando a lista."); await reload(); return; }
    const r = await userStore.update(id, updates);
    if (!r.ok) { alert(r.erro); return; }
    await logAction(acao, email, detalhe);
    await reload();
  };

  const approve = async (email) => {
    const u = users.find(x => x.email === email);
    await aplicar(email, {
      status: "aprovado",
      approvedBy: currentUser.email, approvedAt: new Date().toISOString(),
      permissoes: { todas: true, obras: [], viewOnly: false }
    }, "aprovou cadastro", u?.nome || "");
  };

  const reject = async (email) => {
    if (!window.confirm("Rejeitar este cadastro? O acesso é removido e não pode ser desfeito.")) return;
    const id = idDe(email);
    if (!id) return;
    const r = await userStore.remove(id);
    if (!r.ok) { alert(r.erro); return; }
    await logAction("rejeitou cadastro", email);
    await reload();
  };

  const block = async (email) => {
    if (email === currentUser.email) { alert("Você não pode bloquear sua própria conta."); return; }
    await aplicar(email, { status: "bloqueado" }, "bloqueou usuário");
  };

  const unblock = async (email) =>
    aplicar(email, { status: "aprovado" }, "desbloqueou usuário");

  const del = async (email) => {
    if (email === currentUser.email) { alert("Você não pode excluir sua própria conta."); return; }
    if (!window.confirm("Remover o acesso deste usuário permanentemente?")) return;
    const id = idDe(email);
    if (!id) return;
    const r = await userStore.remove(id);
    if (!r.ok) { alert(r.erro); return; }
    await logAction("excluiu usuário", email);
    await reload();
  };

  const promoteAdmin = async (email) =>
    aplicar(email, { role: "admin", permissoes: { todas: true, obras: [], viewOnly: false } }, "promoveu a admin");

  const demoteAdmin = async (email) => {
    if (email === currentUser.email) { alert("Você não pode rebaixar sua própria conta."); return; }
    const n = await userStore.countActiveAdmins();
    if (n <= 1) { alert("Não é possível rebaixar — o sistema deve ter ao menos um administrador ativo."); return; }
    await aplicar(email, { role: "user" }, "rebaixou admin a usuário");
  };

  // Antes gerava uma senha temporária e a mostrava na tela. Não é mais possível nem
  // desejável: definir a senha de outra pessoa exige a chave secreta do Supabase, que
  // jamais pode ir para o navegador. Agora envia o link oficial de redefinição para o
  // email da pessoa — ninguém, nem o admin, chega a ver a senha dela.
  const resetPassword = async (u) => {
    if (!window.confirm("Enviar link de redefinição de senha para " + u.email + "?")) return;
    const r = await userStore.enviarRedefinicao(u.email);
    if (!r.ok) { alert(r.erro); return; }
    setResetting({ user: u, enviado: true });
    await logAction("enviou link de redefinição", u.email);
  };

  const savePermissoes = async (email, permissoes) => {
    await aplicar(email, { permissoes }, "alterou permissões", JSON.stringify(permissoes));
    setEditing(null);
  };

  // Criar conta para outra pessoa não é possível a partir do navegador: a chamada de
  // cadastro trocaria a sessão do admin pela do novo usuário, e a API administrativa
  // do Supabase exige a chave secreta. O fluxo correto é o de aprovação, que já existe.
  const createUser = () => {
    setUserMsg("⚠ Criação direta não disponível: peça à pessoa para se cadastrar na tela de login. O cadastro aparece aqui em Pendentes para você aprovar e definir as permissões.");
  };
  const createAdmin = () => {
    setAdmMsg("⚠ Para criar um administrador: aprove o cadastro da pessoa na aba Pendentes e depois use \"Promover a admin\". Para criar contas em lote, use Authentication → Users no painel do Supabase.");
  };

  const pendentes = users.filter(u => u.status === "pendente");
  const aprovados = users.filter(u => u.status === "aprovado");
  const bloqueados = users.filter(u => u.status === "bloqueado");
  const recoveriesPend = recoveries.filter(r => r.status === "pendente");

  const TABS = [
    { id: "pendentes", l: "⏳ Pendentes (" + pendentes.length + ")" },
    { id: "aprovados", l: "✅ Aprovados (" + aprovados.length + ")" },
    { id: "bloqueados", l: "🚫 Bloqueados (" + bloqueados.length + ")" },
    { id: "recovery", l: "🔑 Recuperações (" + recoveriesPend.length + ")" },
    { id: "novo-user", l: "➕ Novo Usuário" },
    { id: "novo-admin", l: "👑 Novo Admin" },
    { id: "logs", l: "📋 Log" }
  ];

  const isSelf = (u) => u.email === currentUser.email;

  const UserCard = ({ u }) => (
    <div style={{ background: B.branco, borderRadius: 10, padding: "12px 14px", border: "1px solid " + B.creme, marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>
            {u.nome}
            {u.role === "admin" && <span style={{ marginLeft: 6, background: B.dourado, color: B.cafe, padding: "1px 7px", borderRadius: 10, fontSize: 9, fontWeight: 700 }}>ADMIN</span>}
            {u.isFixed && <span style={{ marginLeft: 4, background: B.cafe, color: B.creme, padding: "1px 7px", borderRadius: 10, fontSize: 9, fontWeight: 700 }}>FIXO</span>}
            {isSelf(u) && <span style={{ marginLeft: 4, background: B.verde, color: "white", padding: "1px 7px", borderRadius: 10, fontSize: 9, fontWeight: 700 }}>VOCÊ</span>}
            {u.mustChangePassword && <span style={{ marginLeft: 4, background: B.laranja, color: "white", padding: "1px 7px", borderRadius: 10, fontSize: 9, fontWeight: 700 }}>TROCAR SENHA</span>}
          </div>
          <div style={{ fontSize: 11, color: B.cinza, fontFamily: "'Montserrat',sans-serif", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
          {u.role !== "admin" && u.status === "aprovado" && u.permissoes && (
            <div style={{ fontSize: 10, color: B.caramelo, fontFamily: "'Montserrat',sans-serif", marginTop: 3 }}>
              {u.permissoes.todas ? "🔓 Acesso total" : `🔒 ${u.permissoes.obras?.length || 0} obra(s)`}
              {u.permissoes.viewOnly && " · 👁 Somente visualizar"}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {u.status === "pendente" && (
            <>
              <button onClick={() => approve(u.email)} style={{ padding: "6px 10px", borderRadius: 6, background: B.verde, color: "white", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>✓ Aprovar</button>
              <button onClick={() => reject(u.email)} style={{ padding: "6px 10px", borderRadius: 6, background: "#fdecea", color: B.vermelho, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>✕ Rejeitar</button>
            </>
          )}
          {u.status === "aprovado" && (
            <>
              {u.role !== "admin" && !u.isFixed && <button onClick={() => setEditing(u)} style={{ padding: "6px 10px", borderRadius: 6, background: B.offwhite, color: B.caramelo, border: "1px solid " + B.creme, cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>✏️ Permissões</button>}
              {!u.isFixed && <button onClick={() => resetPassword(u)} style={{ padding: "6px 10px", borderRadius: 6, background: "#fff8ee", color: B.caramelo, border: "1px solid " + B.latte, cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>🔑 Reset Senha</button>}
              {!u.isFixed && !isSelf(u) && (u.role === "user"
                ? <button onClick={() => promoteAdmin(u.email)} style={{ padding: "6px 10px", borderRadius: 6, background: B.dourado, color: B.cafe, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>⬆ Admin</button>
                : <button onClick={() => demoteAdmin(u.email)} style={{ padding: "6px 10px", borderRadius: 6, background: B.offwhite, color: B.caramelo, border: "1px solid " + B.creme, cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>⬇ User</button>)}
              {!u.isFixed && !isSelf(u) && <button onClick={() => block(u.email)} style={{ padding: "6px 10px", borderRadius: 6, background: "#fff0e0", color: B.laranja, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>🚫 Bloquear</button>}
            </>
          )}
          {u.status === "bloqueado" && !u.isFixed && (
            <>
              <button onClick={() => unblock(u.email)} style={{ padding: "6px 10px", borderRadius: 6, background: B.verde, color: "white", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>✓ Desbloquear</button>
              <button onClick={() => del(u.email)} style={{ padding: "6px 10px", borderRadius: 6, background: B.vermelho, color: "white", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>🗑 Excluir</button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <h2 style={{ fontFamily: "'Montserrat',sans-serif", color: B.cafe, margin: "0 0 14px", fontSize: 18, fontWeight: 800 }}>🔐 Usuários e Permissões</h2>
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div style={{ height: 14 }} />

      {tab === "pendentes" && (pendentes.length === 0
        ? <div style={{ textAlign: "center", padding: 30, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}><div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>Nenhuma solicitação pendente</div>
        : pendentes.map(u => <UserCard key={u.email} u={u} />))}
      {tab === "aprovados" && aprovados.map(u => <UserCard key={u.email} u={u} />)}
      {tab === "bloqueados" && (bloqueados.length === 0
        ? <div style={{ textAlign: "center", padding: 30, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>Nenhum usuário bloqueado</div>
        : bloqueados.map(u => <UserCard key={u.email} u={u} />))}

      {tab === "recovery" && (recoveriesPend.length === 0
        ? <div style={{ textAlign: "center", padding: 30, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}><div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>Nenhuma solicitação de recuperação</div>
        : recoveriesPend.map(rec => {
            const u = users.find(x => x.email === rec.email);
            return (
              <div key={rec.id} style={{ background: B.branco, borderRadius: 10, padding: "12px 14px", border: "1px solid " + B.creme, marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>
                      🔑 Solicitação de recuperação
                    </div>
                    <div style={{ fontSize: 11, color: B.caramelo, fontFamily: "'Montserrat',sans-serif", marginTop: 3, fontWeight: 600 }}>{rec.email}</div>
                    <div style={{ fontSize: 10, color: B.cinza, fontFamily: "'Montserrat',sans-serif", marginTop: 2 }}>
                      Solicitado em {new Date(rec.requestedAt).toLocaleString("pt-BR")}
                    </div>
                    {!u && <div style={{ fontSize: 10, color: B.vermelho, fontFamily: "'Montserrat',sans-serif", marginTop: 4, fontWeight: 600 }}>⚠ Email não cadastrado no sistema</div>}
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {u && <button onClick={() => resolveRecovery(rec)} style={{ padding: "6px 10px", borderRadius: 6, background: B.verde, color: "white", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>🔑 Gerar nova senha</button>}
                    <button onClick={() => dismissRecovery(rec.id)} style={{ padding: "6px 10px", borderRadius: 6, background: "#fdecea", color: B.vermelho, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>✕ Descartar</button>
                  </div>
                </div>
              </div>
            );
          }))}

      {tab === "novo-user" && (
        <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: B.cafe, marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>Criar novo usuário</div>
          <div style={{ fontSize: 11, color: B.cinza, marginBottom: 14, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6 }}>Usuários criados pelo admin já são aprovados e têm acesso total a todas as obras (você pode editar permissões depois). Eles serão obrigados a trocar a senha no primeiro login.</div>
          <Inp label="Nome" value={creatingUser.nome} onChange={v => setCreatingUser({ ...creatingUser, nome: v })} />
          <Inp label="Email" value={creatingUser.email} onChange={v => setCreatingUser({ ...creatingUser, email: v })} type="email" />
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>Senha inicial</label>
            <PasswordInput value={creatingUser.senha} onChange={v => setCreatingUser({ ...creatingUser, senha: v })} placeholder="Mínimo 6 caracteres" />
            <button onClick={() => setUserMsg("⚠ Criação direta não disponível — veja a orientação acima.")} style={{ marginTop: 6, background: "none", border: "none", color: B.caramelo, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Montserrat',sans-serif", padding: 0 }}>🎲 Gerar senha aleatória</button>
          </div>
          {userMsg && <div style={{ padding: "9px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: userMsg.startsWith("✅") ? "#e8f5e1" : "#fdecea", color: userMsg.startsWith("✅") ? B.verde : B.vermelho, marginBottom: 10, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.5 }}>{userMsg}</div>}
          <Btn full color={B.verde} onClick={createUser}>+ Criar Usuário</Btn>
        </div>
      )}

      {tab === "novo-admin" && (
        <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: B.cafe, marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>Criar novo administrador</div>
          <div style={{ fontSize: 11, color: B.cinza, marginBottom: 14, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6 }}>Administradores têm acesso total a todas as obras e podem gerenciar usuários. Será obrigado a trocar senha no primeiro login.</div>
          <Inp label="Nome" value={novoAdm.nome} onChange={v => setNovoAdm({ ...novoAdm, nome: v })} />
          <Inp label="Email" value={novoAdm.email} onChange={v => setNovoAdm({ ...novoAdm, email: v })} type="email" />
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>Senha inicial</label>
            <PasswordInput value={novoAdm.senha} onChange={v => setNovoAdm({ ...novoAdm, senha: v })} placeholder="Mínimo 6 caracteres" />
            <button onClick={() => setAdmMsg("⚠ Criação direta não disponível — veja a orientação acima.")} style={{ marginTop: 6, background: "none", border: "none", color: B.caramelo, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Montserrat',sans-serif", padding: 0 }}>🎲 Gerar senha aleatória</button>
          </div>
          {admMsg && <div style={{ padding: "9px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: admMsg.startsWith("✅") ? "#e8f5e1" : "#fdecea", color: admMsg.startsWith("✅") ? B.verde : B.vermelho, marginBottom: 10, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.5 }}>{admMsg}</div>}
          <Btn full color={B.dourado} onClick={createAdmin}>👑 Criar Administrador</Btn>
        </div>
      )}

      {tab === "logs" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {logs.length === 0
            ? <div style={{ textAlign: "center", padding: 30, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>Nenhuma ação registrada</div>
            : [...logs].reverse().slice(0, 50).map(l => (
              <div key={l.id} style={{ background: B.branco, borderRadius: 8, padding: "10px 13px", border: "1px solid " + B.creme }}>
                <div style={{ fontSize: 11, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>
                  <strong>{l.actor}</strong> {l.action} <strong style={{ color: B.caramelo }}>{l.target}</strong>
                </div>
                <div style={{ fontSize: 9, color: B.cinza, fontFamily: "'Montserrat',sans-serif", marginTop: 2 }}>{new Date(l.ts).toLocaleString("pt-BR")}</div>
              </div>
            ))
          }
        </div>
      )}

      {editing && <PermissoesModal user={editing} obras={obras} onSave={(perms) => savePermissoes(editing.email, perms)} onClose={() => setEditing(null)} isMobile={isMobile} />}
      {resetting && <ResetPasswordModal info={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

// Modal que mostra a senha temporária gerada após reset
function ResetPasswordModal({ info, onClose }) {
  const { user, newPassword } = info;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(newPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const sendByMail = () => {
    const subject = encodeURIComponent("Nova senha temporária — Alpha Implantações");
    const body = encodeURIComponent(
      "Olá " + user.nome + ",\n\n" +
      "Sua senha foi redefinida no sistema Alpha Implantações.\n\n" +
      "Email: " + user.email + "\n" +
      "Nova senha temporária: " + newPassword + "\n\n" +
      "No primeiro login, você será solicitado a trocar essa senha por uma definitiva.\n\n" +
      "Acesse: https://claude.ai/...\n\n" +
      "Atenciosamente,\nEquipe Alpha"
    );
    window.location.href = "mailto:" + user.email + "?subject=" + subject + "&body=" + body;
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: B.branco, borderRadius: 14, padding: 22, maxWidth: 420, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔑</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, fontWeight: 800, color: B.cafe }}>Senha redefinida</div>
          <div style={{ fontSize: 12, color: B.cinza, fontFamily: "'Montserrat',sans-serif", marginTop: 4 }}>{user.nome} · {user.email}</div>
        </div>
        <div style={{ background: B.offwhite, border: "2px solid " + B.dourado, borderRadius: 10, padding: "16px", textAlign: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6, fontFamily: "'Montserrat',sans-serif" }}>Nova senha temporária</div>
          <div style={{ fontFamily: "'Montserrat',monospace", fontSize: 22, fontWeight: 800, color: B.cafe, letterSpacing: "2px" }}>{newPassword}</div>
        </div>
        <div style={{ background: "#fff8ee", border: "1px solid " + B.latte, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: B.caramelo, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6, marginBottom: 14 }}>
          ℹ️ O usuário será obrigado a trocar essa senha no próximo login. Copie ou envie por email agora — depois de fechar este aviso, a senha original não fica salva.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn outline color={B.caramelo} onClick={copy}>{copied ? "✓ Copiado" : "📋 Copiar"}</Btn>
          <Btn color={B.cafe} onClick={sendByMail}>✉️ Enviar por email</Btn>
          <Btn full color={B.verde} onClick={onClose}>OK, fechei</Btn>
        </div>
      </div>
    </div>
  );
}

function PermissoesModal({ user, obras, onSave, onClose, isMobile }) {
  const [modo, setModo] = useState(user.permissoes?.todas ? "todas" : "especificas");
  const [obraIds, setObraIds] = useState(user.permissoes?.obras || []);
  const [viewOnly, setViewOnly] = useState(user.permissoes?.viewOnly || false);

  const toggleObra = (id) => setObraIds(obraIds.includes(id) ? obraIds.filter(x => x !== id) : [...obraIds, id]);

  const handleSave = () => onSave({
    todas: modo === "todas",
    obras: modo === "especificas" ? obraIds : [],
    viewOnly
  });

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: B.branco, borderRadius: 14, padding: 20, maxWidth: 440, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 800, color: B.cafe, marginBottom: 4 }}>Editar Permissões</div>
        <div style={{ fontSize: 11, color: B.cinza, marginBottom: 16, fontFamily: "'Montserrat',sans-serif" }}>{user.nome} · {user.email}</div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", marginBottom: 8, fontFamily: "'Montserrat',sans-serif" }}>Escopo de acesso</div>
          <div style={{ display: "flex", gap: 7 }}>
            {[["todas", "🔓 Todas as obras"], ["especificas", "🔒 Obras específicas"]].map(([v, l]) => (
              <button key={v} onClick={() => setModo(v)} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "2px solid " + (modo === v ? B.cafe : B.creme), background: modo === v ? B.cafe : "white", color: modo === v ? B.creme : B.caramelo, cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>{l}</button>
            ))}
          </div>
        </div>

        {modo === "especificas" && (
          <div style={{ marginBottom: 14, maxHeight: 200, overflowY: "auto", border: "1px solid " + B.creme, borderRadius: 8, padding: 6 }}>
            {obras.map(o => (
              <label key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, cursor: "pointer", background: obraIds.includes(o.id) ? B.offwhite : "transparent" }}>
                <input type="checkbox" checked={obraIds.includes(o.id)} onChange={() => toggleObra(o.id)} />
                <span style={{ fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif", flex: 1 }}>{o.nome}</span>
              </label>
            ))}
          </div>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: viewOnly ? "#fff8ee" : B.offwhite, cursor: "pointer", marginBottom: 16, border: "1px solid " + (viewOnly ? B.latte : B.creme) }}>
          <input type="checkbox" checked={viewOnly} onChange={e => setViewOnly(e.target.checked)} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>👁 Somente visualização</div>
            <div style={{ fontSize: 10, color: B.cinza, fontFamily: "'Montserrat',sans-serif", marginTop: 1 }}>Usuário pode ver mas não editar nada</div>
          </div>
        </label>

        <div style={{ display: "flex", gap: 8 }}>
          <Btn outline color={B.cinza} onClick={onClose}>Cancelar</Btn>
          <Btn full color={B.verde} onClick={handleSave}>💾 Salvar</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── ACCESS DENIED ────────────────────────────────────────────────────────────
function AccessDenied() {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: B.cafe, marginBottom: 6 }}>Acesso restrito</div>
      <div style={{ fontSize: 12 }}>Você não tem permissão para acessar esta área.</div>
    </div>
  );
}

// ─── SIDEBAR ─────────────────────────────────────────────────────────────────
function Sidebar({ view, setView, isMobile, open, onClose, alertCount, user, onLogout, recoveryCount }) {
  const admin = isAdmin(user);
  const visitor = user?.isVisitor;
  const canEditFull = !visitor && (admin || (user?.permissoes?.todas && !user?.permissoes?.viewOnly));
  const NAV = visitor
    ? [{ id: "home", l: "Dashboard", i: "🏠" }]
    : [
        { id: "home", l: "Dashboard", i: "🏠" },
        ...(canEditFull ? [{ id: "nova", l: "Nova Obra", i: "➕" }] : []),
        { id: "equipe", l: "Equipe", i: "👥" },
        { id: "relatorios", l: "Relatórios", i: "📄" },
        ...(admin ? [{ id: "whatsapp", l: "WhatsApp", i: "💬" }] : []),
        ...(admin ? [{ id: "usuarios", l: "Usuários", i: "🔐" }] : []),
        ...(admin ? [{ id: "config", l: "Configurações", i: "⚙️" }] : [])
      ];
  const go = (id) => { setView(id); if (isMobile) onClose(); };

  const inner = (
    <div style={{ width: 220, height: "100%", background: B.cafe, display: "flex", flexDirection: "column" }}>
      {isMobile && (
        <div style={{ padding: "14px 16px 0", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: B.dourado, fontSize: 24, lineHeight: 1, padding: 4 }}>✕</button>
        </div>
      )}
      <div style={{ padding: isMobile ? "10px 20px 20px" : "22px 20px", borderBottom: "1px solid rgba(196,164,120,.2)" }}>
        <div style={{ fontSize: 8, color: B.dourado, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 3, fontFamily: "'Montserrat',sans-serif" }}>Grupo Alpha</div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.creme, fontWeight: 800, lineHeight: 1.3 }}>Alpha<br />Implantações</div>
        <div style={{ fontSize: 8, color: B.dourado + "77", marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>v2.3</div>
      </div>
      <nav style={{ padding: "10px 8px", flex: 1, overflowY: "auto" }}>
        {NAV.map(item => (
          <button key={item.id} onClick={() => go(item.id)} style={{
            width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 8, border: "none",
            background: view === item.id ? "rgba(196,164,120,.2)" : "transparent",
            color: view === item.id ? B.dourado : "rgba(245,235,224,.65)",
            cursor: "pointer", fontSize: 13, fontWeight: view === item.id ? 700 : 400,
            fontFamily: "'Montserrat',sans-serif",
            borderLeft: view === item.id ? "3px solid " + B.dourado : "3px solid transparent",
            marginBottom: 3, minHeight: 46, textAlign: "left"
          }}>
            <span style={{ fontSize: 16 }}>{item.i}</span>
            {item.l}
            {item.id === "home" && alertCount > 0 && (
              <span style={{ marginLeft: "auto", background: B.vermelho, color: "white", borderRadius: 10, padding: "2px 7px", fontSize: 10, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>{alertCount}</span>
            )}
            {item.id === "usuarios" && recoveryCount > 0 && (
              <span style={{ marginLeft: "auto", background: B.laranja, color: "white", borderRadius: 10, padding: "2px 7px", fontSize: 10, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>{recoveryCount}</span>
            )}
          </button>
        ))}
      </nav>

      {/* User info + Logout */}
      <div style={{ padding: "12px 14px", borderTop: "1px solid rgba(196,164,120,.2)" }}>
        {user && (
          <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: "rgba(196,164,120,.1)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: B.dourado, fontFamily: "'Montserrat',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
              👤 {user.nome}
              {admin && <span style={{ background: B.dourado, color: B.cafe, padding: "1px 5px", borderRadius: 8, fontSize: 8, fontWeight: 800 }}>ADM</span>}
              {visitor && <span style={{ background: B.cinza, color: "white", padding: "1px 5px", borderRadius: 8, fontSize: 8, fontWeight: 800 }}>DEMO</span>}
            </div>
            <div style={{ fontSize: 9, color: "rgba(245,235,224,.35)", fontFamily: "'Montserrat',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
              {visitor ? "Acesso de visualização" : user.email}
            </div>
          </div>
        )}
        <button
          onClick={onLogout}
          style={{
            width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(176,48,32,.35)",
            background: "rgba(176,48,32,.1)", color: "rgba(255,150,130,.8)", cursor: "pointer",
            fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6
          }}
        >
          <span>⎋</span> {visitor ? "Sair do modo demo" : "Sair"}
        </button>
        <div style={{ fontSize: 9, color: "rgba(245,235,224,.15)", textTransform: "uppercase", letterSpacing: "1px", textAlign: "center", marginTop: 10, fontFamily: "'Montserrat',sans-serif" }}>Ponto Alpha Café</div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 200 }} />}
        <div style={{ position: "fixed", top: 0, left: open ? 0 : -240, bottom: 0, width: 220, zIndex: 201, transition: "left .28s cubic-bezier(.4,0,.2,1)", boxShadow: open ? "4px 0 24px rgba(0,0,0,.3)" : "none" }}>
          {inner}
        </div>
      </>
    );
  }
  return <div style={{ width: 220, minHeight: "100vh", flexShrink: 0 }}>{inner}</div>;
}

// ─── TOP BAR ─────────────────────────────────────────────────────────────────
function TopBar({ isMobile, onMenu, alertCount, view, selObra, user, onLogout }) {
  const titles = { home: "Dashboard", nova: "Nova Obra", equipe: "Equipe", relatorios: "Relatórios", whatsapp: "WhatsApp", usuarios: "Usuários", config: "Configurações", "obra-detail": selObra ? selObra.nome.split("—")[1]?.trim() || selObra.nome : "Implantação" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: isMobile ? "13px 16px 11px" : "0 0 18px", borderBottom: isMobile ? "1px solid " + B.creme : "none", marginBottom: isMobile ? 16 : 0, background: isMobile ? B.branco : "transparent", position: isMobile ? "sticky" : "static", top: 0, zIndex: 10 }}>
      {isMobile && (
        <button onClick={onMenu} style={{ background: "none", border: "1px solid " + B.creme, borderRadius: 8, width: 40, height: 40, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0 }}>☰</button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {isMobile
          ? <div style={{ fontWeight: 700, fontSize: 15, color: B.cafe, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Montserrat',sans-serif" }}>{titles[view] || ""}</div>
          : <div style={{ fontSize: 10, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        }
      </div>
      {alertCount > 0 && <div style={{ background: "#fdecea", border: "1px solid " + B.vermelho + "33", borderRadius: 8, padding: "5px 10px", fontSize: 11, color: B.vermelho, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "'Montserrat',sans-serif" }}>⚠️ {alertCount}</div>}
      {!isMobile && (
        <button
          onClick={onLogout}
          style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(176,48,32,.3)", background: "rgba(176,48,32,.06)", color: B.vermelho, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", display: "flex", alignItems: "center", gap: 5 }}
        >
          ⎋ Sair
        </button>
      )}
    </div>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────
function HomeView({ obras, onSelect, logs, isMobile, templates, onApplyTemplate, user, onDelete }) {
  const [tab, setTab] = useState("dash");
  // Confirmação de exclusão de implantação (guarda a obra escolhida)
  const [excluindo, setExcluindo] = useState(null);
  const [apagando, setApagando] = useState(false);
  const tot = obras.length;
  const atrs = obras.filter(o => o.obraStatus === "Atrasada").length;
  const risco = obras.filter(o => o.obraStatus === "Em Risco").length;
  const avg = Math.round(obras.reduce((a, o) => a + o.avanco, 0) / obras.length);
  const allUrgent = obras.flatMap(o => o.tarefas.filter(t => t.status === "Atrasado" || (daysLeft(t, o.elap) <= 3 && t.status !== "Concluído")).map(t => ({ ...t, obraRef: o, obra: o.nome })));
  const compData = obras.map(o => ({ name: o.nome.split("—")[0].trim().slice(0, 8), avanco: o.avanco }));
  const TABS = [{ id: "dash", l: "📊 Dashboard" }, { id: "urgencias", l: "⚠️ Urgências (" + allUrgent.length + ")" }, { id: "comparativo", l: "📈 Comparativo" }, { id: "logs", l: "📋 Logs" }];

  return (
    <div>
      <div style={{ background: "linear-gradient(135deg," + B.cafe + "," + B.marrom + " 60%," + B.caramelo + ")", borderRadius: 14, padding: isMobile ? "18px" : "24px 26px", marginBottom: 16, color: B.creme, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: -20, top: -20, width: 140, height: 140, borderRadius: "50%", background: "rgba(196,164,120,.07)" }} />
        <div style={{ fontSize: 8, color: B.dourado, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 2, fontFamily: "'Montserrat',sans-serif" }}>Grupo Alpha · Alpha Implantações</div>
        <h1 style={{ margin: "0 0 3px", fontSize: isMobile ? 18 : 22, fontFamily: "'Montserrat',sans-serif", fontWeight: 800 }}>Gestão de Implantações</h1>
        <div style={{ fontSize: 10, color: B.creme + "77", fontFamily: "'Montserrat',sans-serif" }}>{new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        <div style={{ display: "flex", gap: isMobile ? 16 : 24, marginTop: 14, flexWrap: "wrap" }}>
          {[[tot, "Obras"], [avg + "%", "Avanço Médio"], [atrs, "Atrasadas", atrs > 0 ? "#ff9a8a" : B.creme], [risco, "Em Risco", risco > 0 ? "#ffcc88" : B.creme]].map(([v, l, c]) => (
            <div key={l}><div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, fontFamily: "'Montserrat',sans-serif", color: c || B.creme }}>{v}</div><div style={{ fontSize: 9, color: B.dourado, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>{l}</div></div>
          ))}
        </div>
      </div>
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div style={{ height: 14 }} />

      {tab === "dash" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 9, marginBottom: 16 }}>
            <KpiCard label="Total Obras" value={tot} icon="🏗️" color={B.cafe} />
            <KpiCard label="Em Risco" value={risco} icon="⚠️" color={B.laranja} />
            <KpiCard label="Atrasadas" value={atrs} icon="🔴" color={B.vermelho} />
            <KpiCard label="Urgências" value={allUrgent.length} icon="⏰" color={B.laranja} />
            <KpiCard label="Avanço Médio" value={avg + "%"} icon="📈" color={B.caramelo} />
            <KpiCard label="Logs" value={logs.length} icon="📋" color={B.cinza} />
          </div>
          <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.cafe, margin: "0 0 10px", fontWeight: 700 }}>Implantações Ativas</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {obras.map(o => (
              <div key={o.id} onClick={() => onSelect(o)} style={{ background: B.branco, borderRadius: 12, padding: isMobile ? "14px" : "16px 18px", border: "1px solid " + B.creme, cursor: "pointer", borderLeft: "4px solid " + statusColor(o.obraStatus) }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: B.cafe, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Montserrat',sans-serif" }}>{o.nome}</div>
                    <div style={{ fontSize: 11, color: B.cinza, marginTop: 1, fontFamily: "'Montserrat',sans-serif" }}>{o.resp}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>{o.avanco}%</span>
                    <Badge s={o.obraStatus} />
                  </div>
                </div>
                <Bar pct={o.avanco} color={statusColor(o.obraStatus)} h={5} />
                <div style={{ display: "flex", gap: 10, marginTop: 7, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, color: B.verde, fontFamily: "'Montserrat',sans-serif" }}>✅ {o.conc} conc.</span>
                  {o.atras > 0 && <span style={{ fontSize: 10, color: B.vermelho, fontFamily: "'Montserrat',sans-serif" }}>⚠️ {o.atras} atras.</span>}
                  <span style={{ fontSize: 10, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>IDP: {o.idp}</span>
                </div>
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid " + B.creme, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <TemplateSelector obra={o} templates={templates} onApply={tpl => onApplyTemplate(o, tpl)} readOnly={!canEdit(user)} />
                  {isAdmin(user) && !user?.isVisitor && (
                    <button
                      onClick={e => { e.stopPropagation(); setExcluindo(o); }}
                      title="Excluir implantação"
                      style={{ background: "#fdecea", border: "1px solid " + B.vermelho + "33", borderRadius: 6, padding: "5px 9px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: B.vermelho, fontFamily: "'Montserrat',sans-serif", minHeight: 28, flexShrink: 0 }}
                    >🗑 Excluir</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "urgencias" && (
        <div>
          {allUrgent.length === 0
            ? <div style={{ textAlign: "center", padding: 32, color: B.cinza }}><div style={{ fontSize: 28 }}>✅</div><div style={{ fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>Nenhuma urgência!</div></div>
            : allUrgent.map((u, i) => (
              <div key={i} onClick={() => onSelect(u.obraRef)} style={{ background: B.branco, borderRadius: 8, padding: "11px 13px", border: "1px solid " + B.vermelho + "33", marginBottom: 7, cursor: "pointer", borderLeft: "3px solid " + B.vermelho }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{u.t}</div>
                <div style={{ fontSize: 11, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{u.obra} · P{u.p} · {u.progresso}%</div>
              </div>
            ))
          }
        </div>
      )}

      {tab === "comparativo" && (
        <>
          <div style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.cafe, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>Avanço por Obra (%)</div>
            <BarChartSVG data={compData} />
          </div>
          {obras.map((o) => (
            <div key={o.id} onClick={() => onSelect(o)} style={{ background: B.branco, borderRadius: 10, padding: "12px 14px", border: "1px solid " + B.creme, marginBottom: 7, cursor: "pointer", display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: B.cafe, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Montserrat',sans-serif" }}>{o.nome}</div>
                <div style={{ fontSize: 10, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{o.resp}</div>
              </div>
              <Badge s={o.obraStatus} />
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: statusColor(o.obraStatus), fontFamily: "'Montserrat',sans-serif" }}>{o.avanco}%</div>
                <div style={{ fontSize: 9, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>IDP: {o.idp}</div>
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "logs" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {logs.length === 0
            ? <div style={{ textAlign: "center", padding: 28, color: B.cinza, fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>Nenhuma ação registrada.</div>
            : [...logs].reverse().slice(0, 30).map((l, i) => (
              <div key={i} style={{ background: B.branco, borderRadius: 8, padding: "10px 13px", border: "1px solid " + B.creme }}>
                <div style={{ fontSize: 11, color: B.cafe, fontWeight: 600, fontFamily: "'Montserrat',sans-serif" }}>{l.obra} · {l.tarefa}</div>
                <div style={{ fontSize: 10, color: B.cinza, marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>{l.campo}: {String(l.de)} → <strong style={{ color: B.caramelo }}>{String(l.para)}</strong></div>
                <div style={{ fontSize: 9, color: B.cinza, marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>{new Date(l.ts).toLocaleString("pt-BR").slice(0, 16)}</div>
              </div>
            ))
          }
        </div>
      )}

      {/* Confirmação de exclusão de implantação — a exclusão só ocorre após confirmar */}
      {excluindo && (
        <div onClick={() => !apagando && setExcluindo(null)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: B.branco, borderRadius: 14, padding: 22, maxWidth: 420, width: "100%" }}>
            <div style={{ fontSize: 38, textAlign: "center", marginBottom: 10 }}>🗑</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 17, fontWeight: 800, color: B.cafe, textAlign: "center", marginBottom: 6 }}>
              Excluir implantação?
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: B.caramelo, textAlign: "center", marginBottom: 14, fontFamily: "'Montserrat',sans-serif" }}>
              {excluindo.nome}
            </div>
            <div style={{ background: "#fdecea", border: "1px solid " + B.vermelho + "33", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: B.cafe, lineHeight: 1.65, fontFamily: "'Montserrat',sans-serif" }}>
              A exclusão remove esta implantação e os dados relacionados a ela:
              <div style={{ marginTop: 7 }}>
                • <strong>{(excluindo.tarefas || []).length} tarefas</strong> com progresso, comentários e custos<br />
                • <strong>{(excluindo.arquivos || []).length + (excluindo.tarefas || []).reduce((a, t) => a + (t.arquivos?.length || 0), 0)} arquivos</strong> anexados<br />
                • dados do franqueado e contatos desta implantação
              </div>
              <div style={{ marginTop: 9, color: B.vermelho, fontWeight: 700 }}>
                Esta ação não pode ser desfeita, e vale para toda a equipe.
              </div>
              <div style={{ marginTop: 7, fontSize: 11, color: B.cinza, fontWeight: 500 }}>
                As outras implantações e o histórico de atividades não são afetados.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn outline color={B.cinza} disabled={apagando} onClick={() => setExcluindo(null)}>Cancelar</Btn>
              <Btn full color={B.vermelho} disabled={apagando}
                   onClick={async () => {
                     setApagando(true);
                     const res = await onDelete(excluindo);
                     setApagando(false);
                     if (res?.ok) setExcluindo(null);
                     else alert(res?.erro || "Não foi possível excluir a implantação.");
                   }}>
                {apagando ? "⏳ Excluindo..." : "Excluir implantação"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OBRA DETAIL ─────────────────────────────────────────────────────────────
function ObraDetail({ obra, onBack, onUpdate, addLog, isMobile, user, templates, onApplyTemplate }) {
  const [tab, setTab] = useState("overview");
  const [aiOut, setAiOut] = useState({});
  const [aiLoad, setAiLoad] = useState({});
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filterP, setFilterP] = useState("Todos");
  const [filterS, setFilterS] = useState("Todos");
  const [baseline, setBaseline] = useState(false);
  const svgRef = useRef(null);
  const readOnly = !canEdit(user);

  const TABS = [{ id: "overview", l: "📊 Dashboard" }, { id: "tarefas", l: "☑️ Tarefas" }, { id: "gantt", l: "📅 Gantt" }, { id: "arquivos", l: "📁 Arquivos" }, { id: "inaug", l: "🎯 Inauguração" }, { id: "franqueado", l: "👤 Contatos" }, { id: "custos", l: "💰 Custos" }, { id: "ai", l: "🧠 IA" }];

  const runAI = async (k, prompt) => {
    setAiLoad(p => ({ ...p, [k]: true }));
    try { const r = await callAI(prompt); setAiOut(p => ({ ...p, [k]: r })); }
    catch { setAiOut(p => ({ ...p, [k]: "Erro ao conectar." })); }
    setAiLoad(p => ({ ...p, [k]: false }));
  };

  const saveTask = (tid, changes) => {
    const tk = obra.tarefas.find(t => t.id === tid);
    if (tk) Object.entries(changes).forEach(([k, v]) => addLog(obra.nome, tk.t, k, String(tk[k]), String(v)));
    onUpdate({ ...obra, tarefas: obra.tarefas.map(t => t.id === tid ? { ...t, ...changes } : t) });
  };

  const dlSVG = () => {
    if (!svgRef.current) return;
    const d = new XMLSerializer().serializeToString(svgRef.current);
    const a = document.createElement("a");
    a.href = "data:image/svg+xml," + encodeURIComponent(d);
    a.download = "gantt.svg";
    a.click();
  };

  const atrs = obra.tarefas.filter(t => t.status === "Atrasado").map(t => t.t).join(", ");
  const ea = obra.tarefas.filter(t => t.status === "Em Andamento").map(t => t.t).slice(0, 3).join(", ");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: B.caramelo, fontSize: 13, fontWeight: 700, padding: "4px 0", display: "flex", alignItems: "center", gap: 4, fontFamily: "'Montserrat',sans-serif" }}>← Voltar</button>
        <Btn small outline color={B.caramelo} onClick={() => printPDF(obra, aiOut.analysis || "")}>📄 PDF</Btn>
      </div>
      <div style={{ background: "linear-gradient(135deg," + B.cafe + "," + B.marrom + ")", borderRadius: 12, padding: isMobile ? "16px" : "18px 22px", color: B.creme, marginBottom: 14 }}>
        <h2 style={{ margin: "0 0 2px", fontFamily: "'Montserrat',sans-serif", fontSize: isMobile ? 16 : 18, fontWeight: 800 }}>{obra.nome}</h2>
        <div style={{ fontSize: 10, color: B.creme + "77", fontFamily: "'Montserrat',sans-serif" }}>{obra.resp}</div>
        <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Badge s={obra.obraStatus} />
          <span style={{ fontSize: 10, color: B.dourado, fontFamily: "'Montserrat',sans-serif" }}>Dia {obra.elap}/{obra.total}</span>
          <span style={{ fontSize: 10, color: B.dourado, fontFamily: "'Montserrat',sans-serif" }}>Avanço: {obra.avanco}%</span>
          <span style={{ fontSize: 10, color: B.dourado, fontFamily: "'Montserrat',sans-serif" }}>IDP: {obra.idp}</span>
        </div>
        <div style={{ marginTop: 8 }}>
          <TemplateSelector obra={obra} templates={templates} onApply={tpl => onApplyTemplate(obra, tpl)} readOnly={readOnly} dark />
        </div>
      </div>
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div style={{ height: 14 }} />
      {readOnly && (
        <div style={{ background: "#fff8ee", border: "1px solid " + B.latte, borderRadius: 8, padding: "8px 12px", fontSize: 11, color: B.caramelo, fontFamily: "'Montserrat',sans-serif", fontWeight: 600, marginBottom: 12 }}>
          👁 Modo somente visualização — você não pode editar esta implantação
        </div>
      )}

      {tab === "overview" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 9, marginBottom: 16 }}>
            <KpiCard label="Avanço" value={obra.avanco + "%"} icon="📈" color={statusColor(obra.obraStatus)} />
            <KpiCard label="PPC" value={obra.ppc + "%"} icon="✅" color={B.caramelo} />
            <KpiCard label="IDP" value={obra.idp} icon="📊" color={obra.idp >= 1 ? B.verde : B.vermelho} />
            <KpiCard label="Dias" value={obra.elap} icon="📅" color={B.cafe} sub={"de " + obra.total} />
          </div>
          {obra.historico && obra.historico.length > 1 && (
            <div style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: B.cafe, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>Histórico Semanal</div>
              <LineChartSVG data={obra.historico} dataKey="avanco" color={B.caramelo} />
            </div>
          )}
          <div style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.cafe, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>🔮 Previsão de Conclusão com IA</div>
            <Btn full onClick={() => runAI("previsao", "Obra: " + obra.nome + " | Avanço: " + obra.avanco + "% | IDP: " + obra.idp + " | Dias: " + obra.elap + "/" + obra.total + " | Atrasadas: " + obra.atras + ". Calcule: 1) Previsão de conclusão 2) Dias de atraso estimados 3) Como recuperar. Máx 150 palavras.")} disabled={aiLoad.previsao}>
              {aiLoad.previsao ? "⏳ Calculando..." : "🤖 Gerar Previsão com IA"}
            </Btn>
            {aiOut.previsao && <div style={{ marginTop: 12, fontSize: 12, color: B.cafe, lineHeight: 1.7, whiteSpace: "pre-wrap", fontFamily: "'Montserrat',sans-serif" }}>{aiOut.previsao}</div>}
          </div>
          <div style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.cafe, textTransform: "uppercase", marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>Status das Tarefas</div>
            {[["Concluídas", obra.conc, B.verde], ["Em Andamento", obra.emAnd, B.latte], ["Atrasadas", obra.atras, B.vermelho], ["Não Iniciadas", obra.naoIn, B.caramelo]].map(([l, v, c]) => (
              <div key={l} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{l}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'Montserrat',sans-serif" }}>{v}</span>
                </div>
                <Bar pct={v / obra.tarefas.length * 100} color={c} h={5} />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "tarefas" && (
        <div>
          <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 4 }}>
            {["Todos", "Concluído", "Em Andamento", "Atrasado", "Não Iniciado", "Não se aplica"].map(s => (
              <button key={s} onClick={() => setFilterS(s)} style={{ padding: "6px 12px", borderRadius: 16, border: "1px solid " + (filterS === s ? statusColor(s) : B.creme), background: filterS === s ? statusColor(s) : "white", color: filterS === s ? "white" : B.caramelo, cursor: "pointer", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", minHeight: 34, fontFamily: "'Montserrat',sans-serif" }}>{s}</button>
            ))}
          </div>
          <div style={{ background: B.branco, borderRadius: 12, border: "1px solid " + B.creme, overflow: "hidden" }}>
            {obra.tarefas.filter(tk => filterS === "Todos" || tk.status === filterS).map((tk, i) => {
              const blocked = isBlocked(tk, obra.tarefas);
              const dl = daysLeft(tk, obra.elap);
              const editing = editId === tk.id;
              return (
                <div key={tk.id} style={{ borderBottom: "1px solid " + B.creme }}>
                  <div style={{ padding: "11px 13px", background: i % 2 === 0 ? B.offwhite : B.branco, display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: prioColor(tk.p), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "white", flexShrink: 0, marginTop: 1 }}>{tk.p}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: tk.status === "Atrasado" ? B.vermelho : B.cafe, fontWeight: tk.status === "Atrasado" ? 700 : 500, lineHeight: 1.35, fontFamily: "'Montserrat',sans-serif" }}>
                            {tk.bloco ? tk.bloco + " — " : ""}{tk.t}
                            {blocked && <span style={{ marginLeft: 4, fontSize: 10 }}>🔒</span>}
                            {dl <= 3 && dl >= 0 && tk.status !== "Concluído" && <span style={{ marginLeft: 4, fontSize: 10, color: B.laranja }}>⏰{dl}d</span>}
                          </div>
                          {tk.comentario && <div style={{ fontSize: 10, color: B.cinza, marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>💬 {tk.comentario}</div>}
                        </div>
                        <div style={{ display: "flex", gap: 7, alignItems: "center", flexShrink: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: statusColor(tk.status), fontFamily: "'Montserrat',sans-serif" }}>{tk.progresso}%</div>
                          {(tk.arquivos?.length > 0) && <span style={{ fontSize: 10, color: B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>📎{tk.arquivos.length}</span>}
                          {!readOnly && <button onClick={() => { if (editing) { setEditId(null); } else { setEditId(tk.id); setEditData({ status: tk.status, progresso: tk.progresso, comentario: tk.comentario || "", responsavel: tk.responsavel || "", dataPrevista: tk.dataPrevista || "", dataConclusao: tk.dataConclusao || "" }); } }} style={{ background: editing ? B.caramelo : B.offwhite, border: "1px solid " + B.creme, borderRadius: 6, padding: "5px 9px", cursor: "pointer", fontSize: 11, color: editing ? "white" : B.caramelo, fontWeight: 700, minHeight: 32, fontFamily: "'Montserrat',sans-serif" }}>✏️</button>}
                          {readOnly && (tk.arquivos?.length > 0) && <button onClick={() => setEditId(editing ? null : tk.id)} style={{ background: editing ? B.caramelo : B.offwhite, border: "1px solid " + B.creme, borderRadius: 6, padding: "5px 9px", cursor: "pointer", fontSize: 11, color: editing ? "white" : B.caramelo, fontWeight: 700, minHeight: 32, fontFamily: "'Montserrat',sans-serif" }}>📁</button>}
                        </div>
                      </div>
                      <div style={{ marginTop: 5, display: "flex", gap: 7, alignItems: "center" }}>
                        <Badge s={tk.status} />
                        <div style={{ flex: 1 }}><Bar pct={tk.progresso} color={statusColor(tk.status)} h={4} /></div>
                      </div>
                    </div>
                  </div>
                  {editing && (
                    <div style={{ padding: "14px 13px", background: "#fffdf5", borderTop: "1px solid " + B.dourado + "33" }}>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                          <div>
                            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Responsável</label>
                            <div style={{ fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{tk.responsavel || "—"}</div>
                          </div>
                          <div>
                            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Área</label>
                            <div style={{ fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{tk.area || "—"}</div>
                          </div>
                        </div>
                        {tk.descricao && (
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Descrição / Detalhamento</label>
                            <div style={{ fontSize: 12, color: B.cafe, lineHeight: 1.5, fontFamily: "'Montserrat',sans-serif" }}>{tk.descricao}</div>
                          </div>
                        )}
                        {tk.observacoes && (
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Observações</label>
                            <div style={{ fontSize: 12, color: B.cafe, lineHeight: 1.5, fontFamily: "'Montserrat',sans-serif" }}>{tk.observacoes}</div>
                          </div>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Data Prevista</label>
                            {readOnly
                              ? <div style={{ fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{tk.dataPrevista || "—"}</div>
                              : <input type="date" value={editData.dataPrevista || ""} onChange={e => setEditData({ ...editData, dataPrevista: e.target.value })} style={{ width: "100%", padding: "9px", borderRadius: 7, border: "1px solid " + B.creme, fontSize: 12, background: B.offwhite, minHeight: 42, fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box" }} />}
                          </div>
                          <div>
                            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Data Conclusão</label>
                            {readOnly
                              ? <div style={{ fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{tk.dataConclusao || "—"}</div>
                              : <input type="date" value={editData.dataConclusao || ""} onChange={e => setEditData({ ...editData, dataConclusao: e.target.value })} style={{ width: "100%", padding: "9px", borderRadius: 7, border: "1px solid " + B.creme, fontSize: 12, background: B.offwhite, minHeight: 42, fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box" }} />}
                          </div>
                        </div>
                      </div>
                      {!readOnly && (
                        <>
                          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, marginBottom: 10 }}>
                            <div>
                              <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Status</label>
                              <select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })} style={{ width: "100%", padding: "9px", borderRadius: 7, border: "1px solid " + B.creme, fontSize: 12, background: B.offwhite, minHeight: 42, fontFamily: "'Montserrat',sans-serif" }}>
                                {["Não Iniciado", "Em Andamento", "Concluído", "Atrasado", "Não se aplica"].map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>Progresso: {editData.progresso}%</label>
                              <input type="range" min={0} max={100} step={5} value={editData.progresso} onChange={e => setEditData({ ...editData, progresso: Number(e.target.value) })} style={{ width: "100%", marginTop: 10 }} />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 4, textTransform: "uppercase", fontFamily: "'Montserrat',sans-serif" }}>💬 Comentário</label>
                            <textarea value={editData.comentario} onChange={e => setEditData({ ...editData, comentario: e.target.value })} placeholder="Observação ou atualização..." rows={2} style={{ width: "100%", padding: "9px", borderRadius: 7, border: "1px solid " + B.creme, fontSize: 12, fontFamily: "'Montserrat',sans-serif", resize: "vertical", boxSizing: "border-box" }} />
                          </div>
                          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                            <Btn onClick={() => { saveTask(tk.id, editData); setEditId(null); }} color={B.verde}>💾 Salvar</Btn>
                            <Btn outline color={B.cinza} onClick={() => setEditId(null)}>Cancelar</Btn>
                          </div>
                        </>
                      )}
                      <div style={{ borderTop: readOnly ? "none" : "1px solid " + B.creme, paddingTop: readOnly ? 0 : 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", marginBottom: 6, fontFamily: "'Montserrat',sans-serif" }}>📎 Arquivos da Tarefa</div>
                        <FileManager
                          arquivos={tk.arquivos || []}
                          onChange={(arquivos) => saveTask(tk.id, { arquivos })}
                          user={user}
                          readOnly={readOnly}
                          obraId={obra.id}
                          escopo={"tarefa-" + tk.id}
                          compact
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "gantt" && (
        <div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
            <select value={filterP} onChange={e => setFilterP(e.target.value)} style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid " + B.creme, fontSize: 11, background: B.offwhite, flex: 1, minWidth: 100, fontFamily: "'Montserrat',sans-serif" }}>
              <option value="Todos">Todas Prioridades</option>
              {[1, 2, 3, 4, 5, 6, 7].map(p => <option key={p} value={String(p)}>P{p}</option>)}
            </select>
            <select value={filterS} onChange={e => setFilterS(e.target.value)} style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid " + B.creme, fontSize: 11, background: B.offwhite, flex: 1, minWidth: 100, fontFamily: "'Montserrat',sans-serif" }}>
              <option value="Todos">Todos Status</option>
              {["Concluído", "Em Andamento", "Atrasado", "Não Iniciado", "Não se aplica"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: B.caramelo, cursor: "pointer", minHeight: 36, fontFamily: "'Montserrat',sans-serif" }}>
              <input type="checkbox" checked={baseline} onChange={e => setBaseline(e.target.checked)} />Baseline
            </label>
            <button onClick={dlSVG} style={{ padding: "7px 12px", borderRadius: 7, border: "1px solid " + B.creme, background: B.branco, cursor: "pointer", fontSize: 11, fontWeight: 700, color: B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>⬇ SVG</button>
          </div>
          <Gantt tarefas={obra.tarefas} elap={obra.elap} baseline={baseline} filterP={filterP} filterS={filterS} svgRef={svgRef} />
        </div>
      )}

      {tab === "arquivos" && (
        <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.cafe, fontWeight: 700 }}>📁 Arquivos da Obra</div>
              <div style={{ fontSize: 11, color: B.cinza, fontFamily: "'Montserrat',sans-serif", marginTop: 2 }}>PDFs, imagens e vídeos com visualização online</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>{(obra.arquivos || []).length} arquivo(s)</div>
          </div>
          <FileManager
            arquivos={obra.arquivos || []}
            onChange={(arquivos) => onUpdate({ ...obra, arquivos })}
            user={user}
            readOnly={readOnly}
            obraId={obra.id}
            escopo="obra"
          />
        </div>
      )}

      {tab === "inaug" && (
        <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.cafe, fontWeight: 700 }}>Checklist de Inauguração</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: obra.inauguracao.filter(i => i.done).length === obra.inauguracao.length ? B.verde : B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>{obra.inauguracao.filter(i => i.done).length}/{obra.inauguracao.length}</div>
          </div>
          <Bar pct={obra.inauguracao.filter(i => i.done).length / obra.inauguracao.length * 100} color={B.latte} h={6} />
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            {obra.inauguracao.map(item => (
              <div key={item.id} onClick={() => { if (readOnly) return; onUpdate({ ...obra, inauguracao: obra.inauguracao.map(i => i.id === item.id ? { ...i, done: !i.done } : i) }); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 8, border: "1px solid " + (item.done ? B.verde + "44" : B.creme), background: item.done ? "#e8f5e1" : B.offwhite, cursor: readOnly ? "default" : "pointer", minHeight: 46, opacity: readOnly && !item.done ? 0.8 : 1 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid " + (item.done ? B.verde : B.creme), background: item.done ? B.verde : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {item.done && <span style={{ color: "white", fontSize: 12, fontWeight: 700 }}>✓</span>}
                </div>
                <span style={{ fontSize: 13, color: item.done ? B.verde : B.cafe, textDecoration: item.done ? "line-through" : "none", fontFamily: "'Montserrat',sans-serif" }}>{item.t}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "franqueado" && (
        <FranqueadoTab obra={obra} onUpdate={onUpdate} isMobile={isMobile} />
      )}

      {tab === "custos" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 9, marginBottom: 16 }}>
            <KpiCard label="Previsto" value={fBRL(obra.cp)} icon="📋" color={B.cafe} />
            <KpiCard label="Realizado" value={fBRL(obra.cr)} icon="💰" color={obra.cr > obra.cp ? B.vermelho : B.verde} />
            <KpiCard label="Desvio" value={fBRL(Math.abs(obra.cr - obra.cp))} icon={obra.cr > obra.cp ? "📉" : "📈"} color={obra.cr > obra.cp ? B.vermelho : B.verde} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {obra.tarefas.map((tk, i) => {
              const over = (tk.custo_realizado || 0) > (tk.custo_previsto || 0);
              return (
                <div key={tk.id} style={{ background: i % 2 === 0 ? B.offwhite : B.branco, borderRadius: 8, padding: "10px 13px", border: "1px solid " + B.creme }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 8 }}>
                    <span style={{ fontSize: 12, color: B.cafe, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Montserrat',sans-serif" }}>P{tk.p} — {tk.t}</span>
                    <span style={{ fontSize: 10, color: B.cinza, flexShrink: 0, fontFamily: "'Montserrat',sans-serif" }}>{fBRL(tk.custo_previsto)}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="number" value={tk.custo_realizado || 0} onChange={e => saveTask(tk.id, { custo_realizado: Number(e.target.value) })} style={{ flex: 1, padding: "7px 9px", borderRadius: 6, border: "1px solid " + (over ? B.vermelho : B.creme), fontSize: 12, background: over ? "#fdecea" : B.branco, fontFamily: "'Montserrat',sans-serif", color: over ? B.vermelho : B.cafe, minHeight: 38 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: over ? B.vermelho : B.verde, flexShrink: 0 }}>{over ? "▲" : "✓"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "ai" && (
        <AITab obra={obra} atrs={atrs} ea={ea} aiOut={aiOut} aiLoad={aiLoad} runAI={runAI} />
      )}
    </div>
  );
}

function FranqueadoTab({ obra, onUpdate, isMobile }) {
  const [f, setF] = useState({ franqueado: { ...obra.franqueado }, contato2: { ...obra.contato2 }, contato3: { ...obra.contato3 } });
  const Section = ({ label, k, icon }) => (
    <div style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme, marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>{icon} {label}</div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8 }}>
        {["nome", "telefone", "email", k === "franqueado" ? "cpf" : "cargo"].map(field => (
          <Inp key={field} label={field === "cpf" ? "CPF/CNPJ" : field === "cargo" ? "Cargo" : field.charAt(0).toUpperCase() + field.slice(1)} value={f[k][field] || ""} onChange={v => setF({ ...f, [k]: { ...f[k], [field]: v } })} placeholder={field === "email" ? "email@email.com" : field === "telefone" ? "(00) 00000-0000" : ""} />
        ))}
      </div>
      {f[k].telefone && (
        <a href={"https://wa.me/55" + f[k].telefone.replace(/\D/g, "")} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#25d366", color: "white", borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: "none", marginTop: 6, fontFamily: "'Montserrat',sans-serif" }}>📲 WhatsApp {label}</a>
      )}
    </div>
  );
  return (
    <div>
      <Section label="Franqueado" k="franqueado" icon="👤" />
      <Section label="Contato 2" k="contato2" icon="👥" />
      <Section label="Contato 3" k="contato3" icon="👥" />
      <Btn full color={B.verde} onClick={() => onUpdate({ ...obra, ...f })}>💾 Salvar Contatos</Btn>
    </div>
  );
}

function AITab({ obra, atrs, ea, aiOut, aiLoad, runAI }) {
  const [modo, setModo] = useState("analysis");
  const MODOS = [
    { k: "analysis", l: "🧠 Análise", p: "Especialista em gestão de implantações de franquias.\nObra: " + obra.nome + " | Resp: " + obra.resp + " | Dias: " + obra.elap + "/" + obra.total + " | Avanço: " + obra.avanco + "% | IDP: " + obra.idp + "\nStatus: " + obra.obraStatus + " | Atrasadas: " + obra.atras + " | " + atrs + "\nAndamento: " + ea + "\n1) Situação atual 2) Riscos 3) Prioridades imediatas 4) Próximos passos. Máx 280 palavras." },
    { k: "whatsapp", l: "💬 WhatsApp", p: "Mensagem WhatsApp Business Ponto Alpha Café:\nObra: " + obra.nome + " | Status: " + obra.obraStatus + " | Avanço: " + obra.avanco + "%\nAtrasadas: " + atrs + " | Andamento: " + ea + "\nBreve, emojis, max 200 palavras." },
    { k: "report", l: "📄 Relatório", p: "Relatório executivo Ponto Alpha Café:\nObra: " + obra.nome + " | Cidade: " + obra.cidade + " | Resp: " + obra.resp + "\nDias: " + obra.elap + "/" + obra.total + " | Avanço: " + obra.avanco + "% | IDP: " + obra.idp + " | Status: " + obra.obraStatus + "\nConc: " + obra.conc + "/" + obra.tarefas.length + " | Atrasadas: " + atrs + "\nRelatório profissional com análise, KPIs, riscos, recomendações. Máx 400 palavras." },
  ];
  const M = MODOS.find(m => m.k === modo);
  return (
    <div>
      <div style={{ background: "linear-gradient(135deg," + B.cafe + "," + B.marrom + ")", borderRadius: 12, padding: "15px 18px", color: B.creme, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 800 }}>🧠 Alpha Intelligence</div>
        <div style={{ fontSize: 11, color: B.creme + "77", marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>IA integrada para análise e comunicação.</div>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, overflowX: "auto" }}>
        {MODOS.map(m => (
          <button key={m.k} onClick={() => setModo(m.k)} style={{ padding: "7px 14px", borderRadius: 20, border: "2px solid " + (modo === m.k ? B.cafe : B.creme), background: modo === m.k ? B.cafe : "white", color: modo === m.k ? B.creme : B.caramelo, cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", minHeight: 38, fontFamily: "'Montserrat',sans-serif" }}>{m.l}</button>
        ))}
      </div>
      <Btn full onClick={() => runAI(modo, M.p)} disabled={aiLoad[modo]}>{aiLoad[modo] ? "⏳ Gerando…" : "✨ Gerar com IA"}</Btn>
      {aiOut[modo] && (
        <div style={{ background: B.offwhite, border: "1px solid " + B.creme, borderRadius: 12, padding: 16, marginTop: 14 }}>
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.75, fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{aiOut[modo]}</div>
          <div style={{ display: "flex", gap: 7, marginTop: 12, flexWrap: "wrap" }}>
            <Btn small outline color={B.caramelo} onClick={() => navigator.clipboard?.writeText(aiOut[modo])}>📋 Copiar</Btn>
            {modo === "whatsapp" && <a href={"https://wa.me/?text=" + encodeURIComponent(aiOut[modo])} target="_blank" rel="noreferrer" style={{ padding: "6px 12px", borderRadius: 8, background: "#25d366", color: "white", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "'Montserrat',sans-serif" }}>📲 WhatsApp</a>}
          </div>
        </div>
      )}
    </div>
  );
}

function EquipeView({ obras, logs }) {
  const byResp = useMemo(() => {
    const m = {};
    obras.forEach(o => o.tarefas.forEach(t => {
      const r = t.responsavel || "Sem responsável";
      if (!m[r]) m[r] = { nome: r, tarefas: [], obras: new Set() };
      m[r].tarefas.push({ ...t, obraNome: o.nome });
      m[r].obras.add(o.nome);
    }));
    return Object.values(m).map(r => ({ ...r, obras: Array.from(r.obras), conc: r.tarefas.filter(t => t.status === "Concluído").length, atras: r.tarefas.filter(t => t.status === "Atrasado").length }));
  }, [obras]);

  return (
    <div>
      <h2 style={{ fontFamily: "'Montserrat',sans-serif", color: B.cafe, margin: "0 0 14px", fontSize: 18, fontWeight: 800 }}>👥 Equipe</h2>
      {byResp.map(r => (
        <div key={r.nome} style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{r.nome}</div>
              <div style={{ fontSize: 11, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{r.obras.join(", ")}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <span style={{ fontSize: 11, color: B.verde, fontFamily: "'Montserrat',sans-serif" }}>✅ {r.conc}</span>
              {r.atras > 0 && <span style={{ fontSize: 11, color: B.vermelho, fontFamily: "'Montserrat',sans-serif" }}>⚠️ {r.atras}</span>}
              <span style={{ fontSize: 11, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{r.tarefas.length} total</span>
            </div>
          </div>
          <Bar pct={r.conc / r.tarefas.length * 100} color={r.atras > 0 ? B.laranja : B.verde} h={4} />
          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 5 }}>
            {r.tarefas.filter(t => t.status !== "Concluído").slice(0, 3).map(t => (
              <span key={t.id} style={{ background: statusBg(t.status), color: statusColor(t.status), padding: "2px 8px", borderRadius: 12, fontSize: 10, fontFamily: "'Montserrat',sans-serif" }}>{t.t.slice(0, 25)}</span>
            ))}
          </div>
        </div>
      ))}
      <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, color: B.cafe, margin: "16px 0 10px", fontWeight: 700 }}>Log de Ações</h3>
      {logs.length === 0
        ? <div style={{ textAlign: "center", padding: 24, color: B.cinza, fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>Nenhuma ação registrada.</div>
        : [...logs].reverse().slice(0, 20).map((l, i) => (
          <div key={i} style={{ background: B.branco, borderRadius: 8, padding: "10px 13px", border: "1px solid " + B.creme, marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: B.cafe, fontWeight: 600, fontFamily: "'Montserrat',sans-serif" }}>{l.obra} · {l.tarefa}</div>
            <div style={{ fontSize: 10, color: B.cinza, marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>{l.campo}: {l.de} → <strong style={{ color: B.caramelo }}>{l.para}</strong></div>
            <div style={{ fontSize: 9, color: B.cinza, marginTop: 2, fontFamily: "'Montserrat',sans-serif" }}>{new Date(l.ts).toLocaleString("pt-BR").slice(0, 16)}</div>
          </div>
        ))
      }
    </div>
  );
}

function RelatoriosView({ obras }) {
  const [tipo, setTipo] = useState("mensal");
  const [out, setOut] = useState("");
  const [load, setLoad] = useState(false);
  const TIPOS = [{ id: "mensal", l: "📅 Mensal" }, { id: "comparativo", l: "📊 Comparativo" }, { id: "urgencias", l: "⚠️ Urgências" }, { id: "executivo", l: "🎯 Executivo" }];
  const run = async () => {
    setLoad(true); setOut("");
    const base = "Ponto Alpha Café — " + obras.length + " obras — Avanço médio: " + Math.round(obras.reduce((a, o) => a + o.avanco, 0) / obras.length) + "%\n" + obras.map(o => o.nome + ": " + o.avanco + "% (" + o.obraStatus + ") IDP:" + o.idp + " Atras:" + o.atras).join("\n");
    const prompts = { mensal: "Relatório mensal consolidado. " + base + "\nAnálise mensal, KPIs, realizações, próximas ações. Máx 400 palavras.", comparativo: "Comparativo entre implantações. " + base + "\nCompare IDP, PPC, avanço, performance. Máx 350 palavras.", urgencias: "Relatório de urgências. " + base + "\nFoco em tarefas críticas, atrasos, ações imediatas. Máx 300 palavras.", executivo: "Resumo executivo para sócios. " + base + "\nVisão estratégica, riscos, oportunidades, decisões. Máx 300 palavras." };
    try { const r = await callAI(prompts[tipo]); setOut(r); } catch { setOut("Erro."); }
    setLoad(false);
  };
  return (
    <div>
      <h2 style={{ fontFamily: "'Montserrat',sans-serif", color: B.cafe, margin: "0 0 14px", fontSize: 18, fontWeight: 800 }}>📄 Relatórios</h2>
      <Tabs tabs={TIPOS} active={tipo} onChange={setTipo} />
      <div style={{ height: 14 }} />
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <Btn onClick={run} disabled={load}>{load ? "⏳ Gerando..." : "📄 Gerar com IA"}</Btn>
        {out && <Btn outline color={B.caramelo} onClick={() => navigator.clipboard?.writeText(out)}>📋 Copiar</Btn>}
      </div>
      {out && <div style={{ background: B.offwhite, border: "1px solid " + B.creme, borderRadius: 12, padding: 18, whiteSpace: "pre-wrap", lineHeight: 1.8, fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{out}</div>}
    </div>
  );
}

function WhatsAppView({ obras, contatos, setContatos, agend, setAgend }) {
  const [tab, setTab] = useState("envio");
  const [selId, setSelId] = useState(obras[0].id);
  const [msg, setMsg] = useState("");
  const [load, setLoad] = useState(false);
  const [nc, setNc] = useState({ nome: "", telefone: "", tipo: "Franqueado", obra_id: "" });
  const obra = obras.find(o => o.id === selId);
  const DIAS = [{ id: "seg", l: "Segunda" }, { id: "qua", l: "Quarta" }, { id: "sex", l: "Sexta" }];

  const gen = async () => {
    setLoad(true); setMsg("");
    try {
      const r = await callAI("Mensagem WhatsApp Business Ponto Alpha Café.\nObra: " + obra.nome + " | Status: " + obra.obraStatus + " | Avanço: " + obra.avanco + "%\nAtrasadas: " + obra.tarefas.filter(t => t.status === "Atrasado").map(t => t.t).join(", ") + "\nAndamento: " + obra.tarefas.filter(t => t.status === "Em Andamento").map(t => t.t).slice(0, 3).join(", ") + "\nBreve, emojis, max 200 palavras.");
      setMsg(r);
    } catch { setMsg("Erro."); }
    setLoad(false);
  };

  return (
    <div>
      <h2 style={{ fontFamily: "'Montserrat',sans-serif", color: B.cafe, margin: "0 0 14px", fontSize: 18, fontWeight: 800 }}>💬 WhatsApp</h2>
      <Tabs tabs={[{ id: "envio", l: "📤 Envio" }, { id: "agendamento", l: "⏰ Agendamento" }, { id: "contatos", l: "👥 Contatos" }]} active={tab} onChange={setTab} />
      <div style={{ height: 14 }} />

      {tab === "envio" && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>Obra</label>
            <select value={selId} onChange={e => setSelId(Number(e.target.value))} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + B.creme, fontSize: 13, background: B.offwhite, minHeight: 44, fontFamily: "'Montserrat',sans-serif" }}>
              {obras.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
            </select>
          </div>
          <Btn full onClick={gen} disabled={load} color="#25d366">{load ? "⏳ Gerando..." : "📱 Gerar Mensagem com IA"}</Btn>
          {msg && (
            <>
              <div style={{ background: "#e9ffdb", borderRadius: 12, padding: 14, fontSize: 12, lineHeight: 1.7, color: "#1a1a1a", whiteSpace: "pre-wrap", marginTop: 12, marginBottom: 10, border: "1px solid #c3f0c8", fontFamily: "'Montserrat',sans-serif" }}>{msg}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn small outline color={B.caramelo} onClick={() => navigator.clipboard?.writeText(msg)}>📋 Copiar</Btn>
                <a href={"https://wa.me/?text=" + encodeURIComponent(msg)} target="_blank" rel="noreferrer" style={{ padding: "6px 12px", borderRadius: 8, background: "#25d366", color: "white", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "'Montserrat',sans-serif" }}>📲 Abrir WhatsApp</a>
                {obra && obra.franqueado && obra.franqueado.telefone && (
                  <a href={"https://wa.me/55" + obra.franqueado.telefone.replace(/\D/g, "") + "?text=" + encodeURIComponent(msg)} target="_blank" rel="noreferrer" style={{ padding: "6px 12px", borderRadius: 8, background: B.cafe, color: B.creme, fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "'Montserrat',sans-serif" }}>📲 Franqueado</a>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "agendamento" && (
        <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>Envio Automático (08:00)</div>
            <div onClick={() => setAgend({ ...agend, ativo: !agend.ativo })} style={{ width: 46, height: 26, borderRadius: 13, background: agend.ativo ? B.verde : B.creme, position: "relative", cursor: "pointer", transition: "background .2s" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: "white", position: "absolute", top: 3, left: agend.ativo ? 23 : 3, transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", marginBottom: 8, fontFamily: "'Montserrat',sans-serif" }}>Dias de Envio</div>
            <div style={{ display: "flex", gap: 8 }}>
              {DIAS.map(d => (
                <button key={d.id} onClick={() => { const dias = agend.dias.includes(d.id) ? agend.dias.filter(x => x !== d.id) : [...agend.dias, d.id]; setAgend({ ...agend, dias }); }} style={{ flex: 1, padding: "9px", borderRadius: 10, border: "2px solid " + (agend.dias.includes(d.id) ? B.cafe : B.creme), background: agend.dias.includes(d.id) ? B.cafe : "white", color: agend.dias.includes(d.id) ? B.creme : B.caramelo, cursor: "pointer", fontSize: 12, fontWeight: 700, minHeight: 42, fontFamily: "'Montserrat',sans-serif" }}>{d.l}</button>
              ))}
            </div>
          </div>
          <div style={{ background: B.offwhite, borderRadius: 8, padding: 12, fontSize: 11, color: B.caramelo, lineHeight: 1.7, marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>
            <div>Status: <strong>{agend.ativo ? "Agendado ✓" : "Inativo"}</strong></div>
            <div>Próximo envio: <strong>{agend.ativo && agend.dias.length > 0 ? (DIAS.find(d => agend.dias.includes(d.id))?.l || "-") + " às 08:00" : "—"}</strong></div>
            {agend.ultimoEnvio && <div>Último: <strong>{new Date(agend.ultimoEnvio).toLocaleString("pt-BR").slice(0, 16)}</strong></div>}
          </div>
          <Btn full onClick={async () => { setLoad(true); try { const r = await callAI("Resumo semanal WhatsApp Ponto Alpha Café.\n" + obras.map(o => o.nome + ": " + o.avanco + "% (" + o.obraStatus + ")").join("\n") + "\nBreve, profissional, emojis. Máx 200 palavras."); setMsg(r); setAgend({ ...agend, ultimoEnvio: new Date().toISOString() }); setTab("envio"); } catch {} setLoad(false); }} disabled={load}>{load ? "⏳ Gerando..." : "📤 Gerar e Enviar Agora"}</Btn>
        </div>
      )}

      {tab === "contatos" && (
        <div>
          <div style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>Novo Contato</div>
            <Inp label="Nome" value={nc.nome} onChange={v => setNc({ ...nc, nome: v })} />
            <Inp label="Telefone" value={nc.telefone} onChange={v => setNc({ ...nc, telefone: v })} placeholder="(00) 00000-0000" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>Tipo</label>
                <select value={nc.tipo} onChange={e => setNc({ ...nc, tipo: e.target.value })} style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid " + B.creme, fontSize: 12, background: B.offwhite, minHeight: 42, fontFamily: "'Montserrat',sans-serif" }}>
                  {["Franqueado", "Responsável", "Engenheiro", "Arquiteto", "Equipe"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>Obra</label>
                <select value={nc.obra_id} onChange={e => setNc({ ...nc, obra_id: e.target.value })} style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid " + B.creme, fontSize: 12, background: B.offwhite, minHeight: 42, fontFamily: "'Montserrat',sans-serif" }}>
                  <option value="">Todas</option>
                  {obras.map(o => <option key={o.id} value={o.id}>{o.nome.split("—")[0].trim()}</option>)}
                </select>
              </div>
            </div>
            <div style={{ height: 10 }} />
            <Btn full color={B.verde} onClick={() => { if (!nc.nome || !nc.telefone) return; setContatos([...contatos, { ...nc, id: Date.now() }]); setNc({ nome: "", telefone: "", tipo: "Franqueado", obra_id: "" }); }}>+ Adicionar Contato</Btn>
          </div>
          {contatos.length === 0
            ? <div style={{ textAlign: "center", padding: 24, color: B.cinza, fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>Nenhum contato cadastrado.</div>
            : contatos.map(c => (
              <div key={c.id} style={{ background: B.branco, borderRadius: 8, padding: "11px 13px", border: "1px solid " + B.creme, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{c.nome} <span style={{ fontSize: 10, color: B.caramelo }}>({c.tipo})</span></div>
                  <div style={{ fontSize: 11, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{c.telefone}</div>
                </div>
                <div style={{ display: "flex", gap: 7 }}>
                  <a href={"https://wa.me/55" + c.telefone.replace(/\D/g, "")} target="_blank" rel="noreferrer" style={{ padding: "7px 11px", borderRadius: 7, background: "#25d366", color: "white", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", minHeight: 36 }}>📲</a>
                  <button onClick={() => setContatos(contatos.filter(x => x.id !== c.id))} style={{ padding: "7px 11px", borderRadius: 7, background: "#fdecea", border: "none", cursor: "pointer", fontSize: 11, color: B.vermelho, fontWeight: 700, minHeight: 36, fontFamily: "'Montserrat',sans-serif" }}>✕</button>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// Parser de uma linha CSV com suporte a campos entre aspas (que podem conter o próprio delimitador).
// Necessário porque colunas como "Descrição / Detalhamento" e "Observações" são texto livre e
// frequentemente contêm vírgulas — um split(",") ingênuo desalinha as colunas seguintes.
function parseCSVLine(line, delimiter) {
  const result = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else cur += c;
    } else if (c === '"') inQuotes = true;
    else if (c === delimiter) { result.push(cur); cur = ""; }
    else cur += c;
  }
  result.push(cur);
  return result.map(v => v.trim());
}

// Colunas esperadas na planilha de template. "Nº" identifica a tarefa/linha (a quantidade de
// tarefas = quantidade de linhas com "Nº" preenchido); as demais alimentam os dados da tarefa.
const TEMPLATE_COLUMNS = {
  numero: "Nº",
  bloco: "Bloco",
  item: "Item",
  descricao: "Descrição / Detalhamento",
  responsavel: "Responsável",
  status: "Status",
  dataPrevista: "Data Prevista",
  dataConclusao: "Data Conclusão",
  observacoes: "Observações",
  area: "Área"
};

// Normaliza um nome de cabeçalho (maiúsculas/minúsculas, acentos, espaços/quebras de linha
// extras, caracteres invisíveis) SOMENTE para localizar a coluna correta — nunca é aplicada
// aos valores das células, que são lidos e armazenados exatamente como estão na planilha.
function normalizeHeaderKey(h) {
  return String(h || "")
    .normalize("NFD").replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function findColumnIndex(hdrs, canonicalName) {
  const target = normalizeHeaderKey(canonicalName);
  return hdrs.findIndex(h => normalizeHeaderKey(h) === target);
}

// Converte dados já tabulares (matriz de linhas — vindos de CSV ou de uma planilha XLSX já
// lida pela biblioteca de Excel, nunca de bytes brutos do arquivo) em tarefas de template.
// rows[0] é o cabeçalho; rows[1..] são os registros. A coluna "Nº" identifica cada tarefa
// (como um PROCV): só conta como tarefa a linha que tiver "Nº" E "Item" preenchidos — os
// demais campos daquela mesma linha alimentam a tarefa. Retorna { tarefas } ou { erro }.
function rowsToTemplateTasks(rows) {
  if (!rows || rows.length < 2) return { erro: "Arquivo vazio ou sem linhas de dados." };
  const hdrs = rows[0].map(h => String(h ?? "").trim());
  const colIdx = {};
  Object.entries(TEMPLATE_COLUMNS).forEach(([key, name]) => { colIdx[key] = findColumnIndex(hdrs, name); });
  const faltando = [];
  if (colIdx.numero === -1) faltando.push("Nº");
  if (colIdx.item === -1) faltando.push("Item");
  if (faltando.length) {
    return { erro: "Não foi possível localizar a coluna " + faltando.map(c => '"' + c + '"').join(" e ") + " no cabeçalho do arquivo. Colunas encontradas: " + hdrs.filter(Boolean).join(", ") };
  }
  const val = (row, key) => colIdx[key] === -1 ? "" : String(row[colIdx[key]] ?? "").trim();

  const idsUsados = new Set();
  const tarefas = [];
  rows.slice(1).forEach(row => {
    const numeroRaw = val(row, "numero");
    const itemRaw = val(row, "item");
    if (!numeroRaw || !itemRaw) return;
    const id = parseInt(numeroRaw, 10);
    if (!Number.isFinite(id) || idsUsados.has(id)) return;
    idsUsados.add(id);
    tarefas.push({
      id,
      t: itemRaw,
      bloco: val(row, "bloco"),
      descricao: val(row, "descricao"),
      responsavel: val(row, "responsavel"),
      status: val(row, "status"),
      dataPrevista: val(row, "dataPrevista"),
      dataConclusao: val(row, "dataConclusao"),
      observacoes: val(row, "observacoes"),
      area: val(row, "area"),
      p: 1, w: 0.02, s: 0, d: 5, dep: null
    });
  });

  if (!tarefas.length) return { erro: "Nenhuma linha com \"Nº\" e \"Item\" preenchidos foi encontrada. Nenhum template foi criado." };
  return { tarefas };
}

function ConfigView({ obras, templates, setTemplates, setObras, logs, setLogs, contatos, setContatos, agend, setAgend }) {
  const [tab, setTab] = useState("backup");
  const [msg, setMsg] = useState("");
  const [novoTpl, setNovoTpl] = useState({ nome: "", descricao: "" });
  const [backupMsg, setBackupMsg] = useState("");
  const [restoreConfirm, setRestoreConfirm] = useState(null); // { data, fileName }
  const [confirmaTexto, setConfirmaTexto] = useState("");
  const [restaurando, setRestaurando] = useState(false);
  const [selectedObraId, setSelectedObraId] = useState(obras[0]?.id || null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isExcel = /\.xlsx?$/i.test(file.name);
    let rows;
    try {
      if (isExcel) {
        // .xlsx/.xls é um pacote binário (ZIP + XML) — NUNCA lido como texto. A biblioteca
        // "xlsx" (SheetJS) faz o parsing real: workbook → primeira planilha → matriz de linhas.
        const buf = await file.arrayBuffer();
        const workbook = XLSX.read(buf, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
      } else {
        const rawTxt = await file.text();
        // Remove BOM (invisível, gerado por "Salvar como CSV UTF-8" no Excel) — sem isso, o
        // primeiro cabeçalho da planilha não bate com "Item" e todas as linhas caem no fallback.
        const txt = rawTxt.replace(new RegExp("^\\uFEFF"), "");
        const lines = txt.trim().split(/\r?\n/).filter(Boolean);
        if (lines.length < 2) { setMsg("❌ Arquivo inválido."); e.target.value = ""; return; }
        // Detecta o delimitador: Excel em português (BR) costuma exportar CSV com ";" em vez de ",".
        const delimiter = lines[0].split(";").length > lines[0].split(",").length ? ";" : ",";
        rows = lines.map(line => parseCSVLine(line, delimiter));
      }
    } catch (err) {
      setMsg("❌ Não foi possível ler o arquivo (" + (isExcel ? "Excel" : "CSV") + " inválido ou corrompido): " + err.message);
      e.target.value = "";
      return;
    }

    // Localiza as colunas pelo nome (tolerante a maiúsculas/minúsculas, acentos e espaços
    // extras) e monta as tarefas — mesma lógica para CSV e XLSX, já que ambos chegam aqui
    // como uma matriz de linhas (nunca como bytes/texto bruto do arquivo).
    const resultado = rowsToTemplateTasks(rows);
    if (resultado.erro) {
      setMsg("❌ " + resultado.erro);
      e.target.value = "";
      return;
    }

    setMsg("✅ " + resultado.tarefas.length + " tarefas importadas!");
    setTemplates([...templates, { id: Date.now(), nome: file.name.replace(/\.(xlsx?|csv)$/i, ""), descricao: "Importado em " + new Date().toLocaleDateString("pt-BR"), tarefas: resultado.tarefas }]);
    e.target.value = "";
  };

  // ═══ BACKUP COMPLETO ═══════════════════════════════════════════════════════
  const doFullBackup = async () => {
    // Usuários e log administrativo agora vêm do banco, então a leitura é assíncrona.
    const [usersDb, adminLogsDb] = await Promise.all([
      userStore.getAll(), adminLogStore.getAll()
    ]);
    const backup = {
      _meta: {
        version: "v2.7-backup",
        createdAt: new Date().toISOString(),
        appVersion: "Alpha Implantações v2.7",
        totalObras: obras.length,
        totalTarefas: obras.reduce((a, o) => a + o.tarefas.length, 0),
        totalArquivos: obras.reduce((a, o) => a + (o.arquivos?.length || 0) + o.tarefas.reduce((b, t) => b + (t.arquivos?.length || 0), 0), 0)
      },
      obras: obras,
      logs: logs || [],
      contatos: contatos || [],
      templates: templates || [],
      agend: agend || null,
      // Também salvo os usuários e configurações administrativas
      users: usersDb,
      adminLogs: adminLogsDb
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = "backup-alpha-implantacoes-" + ts + ".json";
    a.click();
    URL.revokeObjectURL(url);
    setBackupMsg("✅ Backup completo baixado! Guarde o arquivo em local seguro. Total: " + obras.length + " obras, " + backup._meta.totalTarefas + " tarefas, " + backup._meta.totalArquivos + " arquivos.");
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const txt = await file.text();
      const data = JSON.parse(txt);
      if (!data._meta || !data.obras) { setBackupMsg("❌ Arquivo de backup inválido."); return; }
      setRestoreConfirm({ data, fileName: file.name });
      setBackupMsg("");
    } catch (err) {
      setBackupMsg("❌ Não foi possível ler o arquivo: " + err.message);
    }
    e.target.value = ""; // permite recarregar mesmo arquivo
  };

  // ATENÇÃO: numa base compartilhada, restaurar deixou de ser uma ação local e
  // inofensiva — ela SOBRESCREVE os dados de toda a equipe. Por isso: só admin,
  // confirmação digitada, e um snapshot automático antes de qualquer escrita.
  const confirmRestore = async () => {
    const { data } = restoreConfirm;

    if (confirmaTexto.trim().toUpperCase() !== "RESTAURAR") {
      setBackupMsg("❌ Digite RESTAURAR para confirmar.");
      return;
    }

    setRestaurando(true);
    setBackupMsg("⏳ Salvando snapshot da base atual...");
    const snap = await dataStore.snapshotAntesDeRestaurar();

    setBackupMsg("⏳ Restaurando " + (data.obras?.length || 0) + " obras...");
    const res = await dataStore.substituirTudo({
      obras: data.obras, logs: data.logs, contatos: data.contatos,
      templates: data.templates, agend: data.agend
    });
    setRestaurando(false);

    if (!res.ok) {
      setBackupMsg("❌ Falha na restauração: " + res.erro + " — nada foi alterado além do snapshot.");
      return;
    }

    // Usuários e permissões NÃO são restaurados do arquivo: as contas agora vivem
    // no Supabase Auth, e sobrescrevê-las por um JSON antigo tiraria o acesso de
    // quem foi cadastrado depois do backup.
    setRestoreConfirm(null);
    setConfirmaTexto("");
    setBackupMsg("✅ Restauração concluída na base compartilhada (" + (data.obras?.length || 0) +
      " obras). Snapshot dos " + snap.obras + " registros anteriores guardado. " +
      "As contas de usuário não foram alteradas.");
    setTimeout(() => { if (window.confirm("Recarregar a página para ver os dados restaurados?")) window.location.reload(); }, 1200);
  };

  // ═══ EXPORTAR OBRA ESPECÍFICA (CSV) ════════════════════════════════════════
  const exportObraCSV = () => {
    const obra = obras.find(o => o.id === selectedObraId);
    if (!obra) { setBackupMsg("❌ Selecione uma obra."); return; }
    const hdr = ["Nº","Prioridade","Tarefa","Status","Progresso %","Responsável","Comentário","Custo Previsto","Custo Realizado","Início (dias)","Duração (dias)"];
    const rows = obra.tarefas.map((t, i) => [
      i + 1, t.p, '"' + (t.t || "").replace(/"/g, "'") + '"',
      t.status, t.progresso, '"' + (t.responsavel || "") + '"',
      '"' + (t.comentario || "").replace(/"/g, "'") + '"',
      t.custo_previsto || 0, t.custo_realizado || 0, t.s, t.d
    ].join(","));
    // Cabeçalho da obra
    const metaLines = [
      "OBRA:," + obra.nome,
      "CIDADE:," + (obra.cidade || ""),
      "ENDEREÇO:," + (obra.endereco || ""),
      "RESPONSÁVEL:," + (obra.resp || ""),
      "DATA BASE:," + obra.dataBase,
      "STATUS:," + obra.obraStatus,
      "AVANÇO:," + obra.avanco + "%",
      "IDP:," + obra.idp,
      "PPC:," + obra.ppc + "%",
      "FRANQUEADO:," + (obra.franqueado?.nome || ""),
      "TELEFONE:," + (obra.franqueado?.telefone || ""),
      "EMAIL:," + (obra.franqueado?.email || ""),
      ""
    ];
    const csv = "\uFEFF" + metaLines.join("\n") + hdr.join(",") + "\n" + rows.join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    const safeName = obra.nome.replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").toLowerCase();
    a.download = "obra-" + safeName + "-" + new Date().toISOString().slice(0, 10) + ".csv";
    a.click();
    setBackupMsg("✅ Obra '" + obra.nome + "' exportada em CSV!");
  };

  return (
    <div>
      <h2 style={{ fontFamily: "'Montserrat',sans-serif", color: B.cafe, margin: "0 0 14px", fontSize: 18, fontWeight: 800 }}>⚙️ Configurações</h2>
      <Tabs tabs={[
        { id: "backup", l: "💾 Backup" },
        { id: "import", l: "📥 Importar" },
        { id: "export", l: "📤 Exportar" },
        { id: "templates", l: "📋 Templates" }
      ]} active={tab} onChange={setTab} />
      <div style={{ height: 14 }} />

      {tab === "backup" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Backup Completo */}
          <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "2px solid " + B.dourado }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22 }}>💾</span>
              <div style={{ fontWeight: 800, fontSize: 15, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>Backup Completo do Sistema</div>
            </div>
            <div style={{ fontSize: 12, color: B.cinza, lineHeight: 1.7, marginBottom: 14, fontFamily: "'Montserrat',sans-serif" }}>
              Baixa um arquivo <strong>.json</strong> contendo <strong>tudo</strong>: obras, tarefas, comentários, contatos, arquivos anexados, usuários, permissões, logs e configurações. Guarde em local seguro (Drive, Dropbox, computador).
              <br /><br />
              <strong style={{ color: B.cafe }}>Recomendado antes de qualquer atualização grande do sistema.</strong>
            </div>
            <div style={{ background: B.offwhite, borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 11, color: B.caramelo, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6 }}>
              📊 Dados no sistema agora: <strong>{obras.length} obras</strong> · <strong>{obras.reduce((a, o) => a + o.tarefas.length, 0)} tarefas</strong> · <strong>{obras.reduce((a, o) => a + (o.arquivos?.length || 0) + o.tarefas.reduce((b, t) => b + (t.arquivos?.length || 0), 0), 0)} arquivos</strong> · <strong>{(logs || []).length} registros de log</strong>
            </div>
            <Btn full color={B.dourado} onClick={doFullBackup}>💾 Baixar Backup Completo (.json)</Btn>
          </div>

          {/* Restaurar Backup */}
          <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22 }}>♻️</span>
              <div style={{ fontWeight: 800, fontSize: 15, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>Restaurar Backup</div>
            </div>
            <div style={{ fontSize: 12, color: B.cinza, lineHeight: 1.7, marginBottom: 14, fontFamily: "'Montserrat',sans-serif" }}>
              Restaura um backup anterior a partir de um arquivo <strong>.json</strong>. Isso <strong style={{ color: B.vermelho }}>substitui todos os dados atuais</strong> pelos do arquivo. Use com cuidado.
            </div>
            <label style={{ display: "block", cursor: "pointer", border: "2px dashed " + B.creme, borderRadius: 10, padding: "18px", textAlign: "center", background: B.offwhite }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>📤</div>
              <span style={{ fontSize: 13, fontWeight: 700, color: B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>Selecionar arquivo de backup (.json)</span>
              <input type="file" accept=".json,application/json" onChange={handleRestoreFile} style={{ display: "none" }} />
            </label>
          </div>

          {/* Exportar Obra Específica */}
          <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22 }}>📤</span>
              <div style={{ fontWeight: 800, fontSize: 15, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>Exportar Obra Específica (CSV)</div>
            </div>
            <div style={{ fontSize: 12, color: B.cinza, lineHeight: 1.7, marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>
              Baixa um <strong>.csv</strong> de uma obra específica com todas as tarefas, status, progresso, custos e comentários. Útil para compartilhar com equipe externa ou arquivar.
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>Obra</label>
              <select value={selectedObraId || ""} onChange={e => setSelectedObraId(Number(e.target.value))} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + B.creme, fontSize: 13, background: B.offwhite, minHeight: 42, fontFamily: "'Montserrat',sans-serif" }}>
                {obras.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
              </select>
            </div>
            <Btn full outline color={B.caramelo} onClick={exportObraCSV}>📥 Baixar CSV da Obra</Btn>
          </div>

          {backupMsg && (
            <div style={{ padding: "12px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: backupMsg.startsWith("✅") ? "#e8f5e1" : "#fdecea", color: backupMsg.startsWith("✅") ? B.verde : B.vermelho, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6 }}>
              {backupMsg}
            </div>
          )}
        </div>
      )}

      {tab === "import" && (
        <div style={{ background: B.branco, borderRadius: 12, padding: 20, border: "1px solid " + B.creme }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: B.cafe, marginBottom: 6, fontFamily: "'Montserrat',sans-serif" }}>Importar Planilha (Excel ou CSV)</div>
          <div style={{ fontSize: 12, color: B.cinza, lineHeight: 1.6, marginBottom: 14, fontFamily: "'Montserrat',sans-serif" }}>
            Importe um arquivo <strong>.xlsx</strong> ou <strong>.csv</strong> com a estrutura de tarefas para criar um template de implantação. Colunas reconhecidas: <strong>Nº</strong>, <strong>Bloco</strong>, <strong>Item</strong>, <strong>Descrição / Detalhamento</strong>, <strong>Responsável</strong>, <strong>Status</strong>, <strong>Data Prevista</strong>, <strong>Data Conclusão</strong>, <strong>Observações</strong>, <strong>Área</strong>. A coluna "Nº" identifica cada tarefa — a quantidade de tarefas criadas é igual à quantidade de linhas com "Nº" e "Item" preenchidos (linhas com "Nº" preenchido por numeração automática, mas sem "Item", não contam). A coluna "Bloco" define o número exibido ao lado do título. Todas as colunas exceto "Nº" e "Item" podem ficar em branco.
          </div>
          <div style={{ border: "2px dashed " + B.creme, borderRadius: 10, padding: "24px", textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>📥</div>
            <label style={{ cursor: "pointer", display: "block" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: B.caramelo, fontFamily: "'Montserrat',sans-serif" }}>Selecionar arquivo .xlsx ou .csv</span>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display: "none" }} />
            </label>
          </div>
          {msg && <div style={{ background: msg.startsWith("✅") ? "#e8f5e1" : "#fdecea", padding: 10, borderRadius: 8, fontSize: 12, color: msg.startsWith("✅") ? B.verde : B.vermelho, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>{msg}</div>}
        </div>
      )}

      {tab === "export" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.cafe, marginBottom: 6, fontFamily: "'Montserrat',sans-serif" }}>Modelo CSV para Excel</div>
            <div style={{ fontSize: 12, color: B.cinza, lineHeight: 1.6, marginBottom: 12, fontFamily: "'Montserrat',sans-serif" }}>Modelo com "Nº" e os títulos das 37 tarefas padrão na coluna "Item". Preencha Descrição / Detalhamento, Responsável, Status, Datas, Observações e Área no Excel e reimporte na aba "Importar".</div>
            <Btn full onClick={exportCSV}>⬇️ Baixar Modelo CSV</Btn>
          </div>
          <div style={{ background: B.branco, borderRadius: 12, padding: 18, border: "1px solid " + B.creme }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.cafe, marginBottom: 6, fontFamily: "'Montserrat',sans-serif" }}>PDF Consolidado</div>
            <Btn full outline color={B.caramelo} onClick={() => {
              const w = window.open("", "_blank");
              if (!w) return;
              w.document.write("<!DOCTYPE html><html><head><title>Relatório</title><style>body{font-family:'Montserrat',sans-serif;padding:36px;max-width:760px;margin:0 auto;color:#2c1810}h1{border-bottom:3px solid #c4a478;padding-bottom:8px}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#2c1810;color:#f5ebe0;padding:7px 10px;text-align:left}td{padding:7px 10px;border-bottom:1px solid #f5ebe0}tr:nth-child(even){background:#faf6f1}</style></head><body><h1>Relatório Consolidado — Alpha Implantações</h1><table><tr><th>Obra</th><th>Status</th><th>Avanço</th><th>IDP</th><th>Atrasadas</th></tr>" + obras.map(o => "<tr><td>" + o.nome + "</td><td>" + o.obraStatus + "</td><td>" + o.avanco + "%</td><td>" + o.idp + "</td><td>" + o.atras + "</td></tr>").join("") + "</table></body></html>");
              w.document.close();
              setTimeout(() => w.print(), 400);
            }}>📑 PDF Consolidado</Btn>
          </div>
        </div>
      )}

      {tab === "templates" && (
        <div>
          <div style={{ background: B.branco, borderRadius: 12, padding: 16, border: "1px solid " + B.creme, marginBottom: 12 }}>
            <Inp label="Nome do Template" value={novoTpl.nome} onChange={v => setNovoTpl({ ...novoTpl, nome: v })} placeholder="Ex: Quiosque Shopping" />
            <Btn full color={B.verde} onClick={() => { if (!novoTpl.nome) return; setTemplates([...templates, { id: Date.now(), ...novoTpl, tarefas: TT, origem: "padrao" }]); setNovoTpl({ nome: "", descricao: "" }); }}>+ Criar Template Padrão</Btn>
          </div>
          {[{ id: 0, nome: "Template Padrão", descricao: "37 tarefas oficiais Ponto Alpha", tarefas: TT }, ...templates].map((t, i) => (
            <div key={t.id} style={{ background: B.branco, borderRadius: 8, padding: "12px 14px", border: "1px solid " + B.creme, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 12, color: B.cafe, fontFamily: "'Montserrat',sans-serif" }}>{t.nome}{i === 0 && <span style={{ marginLeft: 6, background: B.creme, color: B.caramelo, padding: "1px 7px", borderRadius: 10, fontSize: 9, fontWeight: 700 }}>OFICIAL</span>}</div>
                <div style={{ fontSize: 11, color: B.cinza, fontFamily: "'Montserrat',sans-serif" }}>{t.descricao} · {(t.tarefas || []).length} tarefas</div>
              </div>
              {i > 0 && <button onClick={() => setTemplates(templates.filter(x => x.id !== t.id))} style={{ padding: "6px 10px", borderRadius: 6, background: "#fdecea", border: "none", cursor: "pointer", fontSize: 11, color: B.vermelho, fontWeight: 700, minHeight: 34, fontFamily: "'Montserrat',sans-serif" }}>✕</button>}
            </div>
          ))}
        </div>
      )}

      {/* Modal de confirmação de restore */}
      {restoreConfirm && (
        <div onClick={() => setRestoreConfirm(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: B.branco, borderRadius: 14, padding: 22, maxWidth: 480, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}>⚠️</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, fontWeight: 800, color: B.cafe, textAlign: "center", marginBottom: 10 }}>Confirmar Restauração</div>
            <div style={{ background: B.offwhite, borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 11, color: B.caramelo, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.7 }}>
              <strong>Arquivo:</strong> {restoreConfirm.fileName}<br />
              <strong>Criado em:</strong> {new Date(restoreConfirm.data._meta.createdAt).toLocaleString("pt-BR")}<br />
              <strong>Versão:</strong> {restoreConfirm.data._meta.appVersion || "?"}<br />
              <strong>Contém:</strong> {restoreConfirm.data._meta.totalObras} obras, {restoreConfirm.data._meta.totalTarefas} tarefas, {restoreConfirm.data._meta.totalArquivos} arquivos
            </div>
            <div style={{ background: "#fdecea", border: "1px solid " + B.vermelho + "44", borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12, color: B.vermelho, fontWeight: 600, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6 }}>
              ⚠️ Isto substitui os dados de <strong>TODA A EQUIPE</strong>, não só os seus. Qualquer obra ou progresso que outra pessoa tenha lançado depois deste backup será perdido.
              <div style={{ marginTop: 8, fontWeight: 500, color: B.cafe }}>
                Um snapshot da base atual é salvo automaticamente antes de sobrescrever, mas avise a equipe antes de continuar.
              </div>
              <div style={{ marginTop: 8, fontWeight: 500, color: B.cafe }}>
                As <strong>contas de usuário não são alteradas</strong> — elas vivem no Supabase e não vêm do arquivo.
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "'Montserrat',sans-serif" }}>
                Digite RESTAURAR para confirmar
              </label>
              <input
                value={confirmaTexto}
                onChange={e => setConfirmaTexto(e.target.value)}
                placeholder="RESTAURAR"
                autoComplete="off"
                style={{ width: "100%", padding: "11px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, letterSpacing: "1px", textAlign: "center", background: B.offwhite, color: B.cafe, fontFamily: "'Montserrat',sans-serif", boxSizing: "border-box",
                         border: "2px solid " + (confirmaTexto.trim().toUpperCase() === "RESTAURAR" ? B.verde : B.creme) }}
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn outline color={B.cinza} onClick={() => { setRestoreConfirm(null); setConfirmaTexto(""); }}>Cancelar</Btn>
              <Btn full color={B.vermelho}
                   disabled={restaurando || confirmaTexto.trim().toUpperCase() !== "RESTAURAR"}
                   onClick={confirmRestore}>
                {restaurando ? "⏳ Restaurando..." : "♻️ Sobrescrever a base"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NovaObra({ onBack, templates, onCriar, isMobile }) {
  const [f, setF] = useState({ nome: "", cidade: "", endereco: "", resp: "", dataBase: "", obs: "" });
  const criar = () => {
    if (!f.nome || !f.dataBase) { alert("Preencha nome e data base."); return; }
    const prog = new Array(37).fill(0);
    const nova = makeObra(Date.now(), f.nome, f.cidade, f.endereco, f.dataBase, f.resp, prog);
    onCriar({ ...nova, obs: f.obs });
    onBack();
  };
  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: B.caramelo, fontSize: 13, fontWeight: 700, padding: "4px 0", marginBottom: 16, fontFamily: "'Montserrat',sans-serif" }}>← Voltar</button>
      <div style={{ background: B.branco, borderRadius: 16, padding: isMobile ? "18px 16px" : "24px", border: "1px solid " + B.creme }}>
        <h2 style={{ fontFamily: "'Montserrat',sans-serif", color: B.cafe, margin: "0 0 20px", fontSize: 18, fontWeight: 800 }}>Nova Implantação</h2>
        <Inp label="Nome da Obra / Unidade" value={f.nome} onChange={v => setF({ ...f, nome: v })} placeholder="Ex: Brasília — Shopping Conjunto Nacional" />
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
          <Inp label="Cidade" value={f.cidade} onChange={v => setF({ ...f, cidade: v })} placeholder="Ex: Brasília-DF" />
          <Inp label="Responsável" value={f.resp} onChange={v => setF({ ...f, resp: v })} placeholder="Nome do responsável" />
        </div>
        <Inp label="Endereço" value={f.endereco} onChange={v => setF({ ...f, endereco: v })} placeholder="Endereço completo" />
        <Inp label="Data Base (Início)" value={f.dataBase} onChange={v => setF({ ...f, dataBase: v })} type="date" />
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: B.caramelo, textTransform: "uppercase", display: "block", marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>Observações</label>
          <textarea value={f.obs} onChange={e => setF({ ...f, obs: e.target.value })} rows={2} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + B.creme, fontSize: 13, fontFamily: "'Montserrat',sans-serif", resize: "vertical", boxSizing: "border-box" }} />
        </div>
        <div style={{ background: B.offwhite, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: B.caramelo, lineHeight: 1.6, fontFamily: "'Montserrat',sans-serif" }}>✨ Sistema gerará automaticamente o checklist com <strong>37 tarefas</strong> e o Gantt.</div>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn outline color={B.cinza} onClick={onBack}>Cancelar</Btn>
          <Btn onClick={criar}>🏗️ Criar Implantação</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── APP ─────────────────────────────────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();
  const [screen, setScreen] = useState("landing"); // landing | login | signup | forgot | pending | change-password | app
  const [user, setUser] = useState(null);
  const [obras, setObras] = useState(OBRAS_DEFAULT);
  const [logs, setLogs] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [agend, setAgend] = useState({ ativo: false, dias: ["seg", "qua", "sex"], hora: "08:00", ultimoEnvio: null });
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState("home");
  const [selObra, setSelObra] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userRefresh, setUserRefresh] = useState(0);


  // Quem pode gravar nos conjuntos globais. Sem esta checagem, um usuário com
  // "somente visualização" tentaria escrever, a RLS recusaria, e o console
  // encheria de erro de permissão sem motivo.
  const podeGravarGlobal = !!user && !user.isVisitor && !user.permissoes?.viewOnly
    && (isAdmin(user) || user.permissoes?.todas);

  // Carrega da base compartilhada, e só depois de haver usuário logado — antes
  // disso a RLS (corretamente) não devolveria nada.
  useEffect(() => {
    if (!user || user.isVisitor) return;
    let vivo = true;
    (async () => {
      const [oo, ll, cc, tt, ag] = await Promise.all([
        dataStore.carregarObras(),
        dataStore.carregarChave("ai-logs-v2", []),
        dataStore.carregarChave("ai-contatos-v2", []),
        dataStore.carregarChave("ai-templates-v2", []),
        dataStore.carregarChave("ai-agend-v2", { ativo: false, dias: ["seg", "qua", "sex"], hora: "08:00", ultimoEnvio: null })
      ]);
      if (!vivo) return;
      // Base vazia NÃO é semeada com as obras de demonstração: numa base
      // compartilhada isso poluiria os dados reais da rede. Ela fica vazia até
      // alguém cadastrar uma obra ou restaurar o backup.
      setObras((oo || []).map(recalc));
      setLogs(ll); setContatos(cc); setTemplates(tt); setAgend(ag);
      setLoaded(true);
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line
  }, [user?.id]);

  // O modo visitante continua local, com as obras de demonstração sanitizadas.
  useEffect(() => {
    if (user?.isVisitor) { setObras(OBRAS_DEFAULT); setLoaded(true); }
  }, [user?.isVisitor]);

  // As obras NÃO são mais salvas como array inteiro — cada alteração grava só a
  // obra alterada, em handleUpdate/handleCriar. Os conjuntos globais seguem
  // salvos por inteiro, que é seguro por terem pouca concorrência.
  useEffect(() => { if (loaded && podeGravarGlobal) dataStore.salvarChave("ai-logs-v2", logs); }, [logs, loaded, podeGravarGlobal]);
  useEffect(() => { if (loaded && podeGravarGlobal) dataStore.salvarChave("ai-contatos-v2", contatos); }, [contatos, loaded, podeGravarGlobal]);
  useEffect(() => { if (loaded && podeGravarGlobal) dataStore.salvarChave("ai-templates-v2", templates); }, [templates, loaded, podeGravarGlobal]);
  useEffect(() => { if (loaded && podeGravarGlobal) dataStore.salvarChave("ai-agend-v2", agend); }, [agend, loaded, podeGravarGlobal]);

  const addLog = useCallback((obraNome, tarefa, campo, de, para) => {
    setLogs(prev => [...prev, { id: Date.now(), ts: new Date().toISOString(), obra: obraNome, tarefa, campo, de, para }]);
  }, []);

  const handleUpdate = useCallback(async (updated) => {
    const r = recalc(updated);
    setObras(prev => prev.map(o => o.id === r.id ? r : o));
    setSelObra(r);
    // Grava só esta obra. É o que impede um usuário de sobrescrever o trabalho
    // do outro, que aconteceria se o array inteiro fosse enviado.
    const res = await dataStore.salvarObra(r);
    if (!res.ok) alert("Não foi possível salvar no servidor: " + res.erro + "\n\nSua alteração aparece na tela mas NÃO foi gravada. Recarregue a página.");
  }, []);

  const handleSelect = useCallback((obra) => {
    if (!canAccessObra(user, obra.id)) return;
    setSelObra(obra); setView("obra-detail");
  }, [user]);
  // Exclusão de implantação. A autorização real está na RLS (política obras_delete,
  // só admin) — este código apenas reflete o resultado. Trocar um id no navegador não
  // dá acesso a nada: o banco recusa e apagarObra devolve erro de permissão.
  const handleExcluirObra = useCallback(async (obra) => {
    const res = await dataStore.apagarObra(obra.id);
    if (!res.ok) return res;
    setObras(prev => prev.filter(o => o.id !== obra.id));
    setSelObra(prev => (prev && prev.id === obra.id ? null : prev));
    return { ok: true };
  }, []);

  const handleCriar = useCallback(async (nova) => {
    const res = await dataStore.criarObra(nova);
    if (!res.ok) { alert("Não foi possível criar a obra no servidor: " + res.erro); return; }
    setObras(prev => [...prev, nova]);
  }, []);
  const handleApplyTemplate = useCallback((obra, template) => {
    handleUpdate(applyTemplateToObra(obra, template));
  }, [handleUpdate]);
  const handleLogout = async () => {
    await userStore.logout();
    setUser(null); setView("home"); setSidebarOpen(false); setScreen("landing");
  };
  const handleVisitor = () => {
    setUser({ isVisitor: true, nome: "Visitante", email: "demo@visualizacao" });
    setView("home");
    setScreen("app");
  };
  const handleLogin = (u) => {
    setUser(u);
    if (u.status === "pendente") setScreen("pending");
    else if (u.mustChangePassword) setScreen("change-password");
    else setScreen("app");
  };
  const handlePasswordChanged = (updatedUser) => {
    setUser(updatedUser);
    setScreen("app");
    setUserRefresh(x => x + 1);
  };

  // Restaura a sessão ao abrir ou recarregar a página, e trata o retorno do link de
  // recuperação de senha enviado por email. Sem isto, um F5 jogaria o usuário de volta
  // para a landing mesmo com a sessão viva, e o link do email não levaria a lugar nenhum.
  const [authPronto, setAuthPronto] = useState(false);
  useEffect(() => {
    let vivo = true;

    (async () => {
      const perfil = await userStore.getCurrent();
      if (!vivo) return;
      if (perfil) {
        setUser(perfil);
        if (perfil.status === "pendente") setScreen("pending");
        else if (perfil.status === "bloqueado") { await userStore.logout(); setUser(null); setScreen("landing"); }
        else if (perfil.mustChangePassword) setScreen("change-password");
        else setScreen("app");
      }
      setAuthPronto(true);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (evento) => {
      if (!vivo) return;
      if (evento === "PASSWORD_RECOVERY") {
        // Chegou pelo link do email: leva direto para definir a nova senha.
        const perfil = await userStore.getCurrent();
        setUser(perfil || { nome: "", email: "", permissoes: {} });
        setScreen("change-password");
      } else if (evento === "SIGNED_OUT") {
        setUser(null);
        setScreen("landing");
      }
    });

    return () => { vivo = false; sub?.subscription?.unsubscribe(); };
    // eslint-disable-next-line
  }, []);

  const obrasVisiveis = useMemo(() => {
    const filtered = visibleObras(user, obras);
    return user?.isVisitor ? sanitizeForVisitor(filtered) : filtered;
  }, [user, obras]);
  const alertCount = user?.isVisitor ? 0 : obrasVisiveis.reduce((a, o) => a + o.tarefas.filter(t => t.status === "Atrasado" || (daysLeft(t, o.elap) <= 3 && t.status !== "Concluído")).length, 0);
  const recoveryCount = 0; // recuperação agora é automática por email

  // ── Screens ────────────────────────────────────────────────────────────────
  if (screen === "landing") return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Montserrat',sans-serif}`}</style>
      <LandingScreen onAccess={() => setScreen("login")} onVisitor={handleVisitor} />
    </>
  );

  if (screen === "login") return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Montserrat',sans-serif}`}</style>
      <LoginScreen onLogin={handleLogin} onSignup={() => setScreen("signup")} onForgot={() => setScreen("forgot")} />
    </>
  );

  if (screen === "pending") return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Montserrat',sans-serif}`}</style>
      <PendingApprovalScreen user={user} onLogout={handleLogout} />
    </>
  );

  if (screen === "change-password") return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Montserrat',sans-serif}`}</style>
      <ChangePasswordScreen user={user} onDone={handlePasswordChanged} onLogout={handleLogout} />
    </>
  );

  if (screen === "forgot") return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Montserrat',sans-serif}`}</style>
      <ForgotPasswordScreen onBack={() => setScreen("login")} />
    </>
  );

  if (screen === "signup") return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Montserrat',sans-serif}`}</style>
      <SignupScreen onBack={() => setScreen("login")} onCadastrar={() => setScreen("login")} />
    </>
  );

  // ── Main App ───────────────────────────────────────────────────────────────
  if (!loaded) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'Montserrat',sans-serif", color: B.caramelo }}>⏳ Carregando…</div>;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Montserrat', sans-serif; background: ${B.offwhite}; }
        button, input, select, textarea { font-family: 'Montserrat', sans-serif; outline: none; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: ${B.creme}; }
        ::-webkit-scrollbar-thumb { background: ${B.caramelo}; border-radius: 3px; }
      `}</style>
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <Sidebar view={view} setView={setView} isMobile={isMobile} open={sidebarOpen} onClose={() => setSidebarOpen(false)} alertCount={alertCount} user={user} onLogout={handleLogout} recoveryCount={recoveryCount} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {user?.isVisitor && (
            <div style={{ background: "linear-gradient(90deg, " + B.dourado + ", " + B.latte + ")", color: B.cafe, padding: "8px 16px", fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", textAlign: "center" }}>
              👁 MODO VISITANTE — visualização demonstrativa · dados pessoais ocultos · <button onClick={() => { setUser(null); setScreen("login"); }} style={{ background: "rgba(44,24,16,.15)", border: "none", color: B.cafe, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", marginLeft: 6, fontFamily: "'Montserrat',sans-serif" }}>Fazer login para acesso completo →</button>
            </div>
          )}
          <TopBar isMobile={isMobile} onMenu={() => setSidebarOpen(true)} alertCount={alertCount} view={view} selObra={selObra} user={user} onLogout={handleLogout} />
          <div style={{ flex: 1, padding: isMobile ? "0 16px 24px" : "0 26px 24px", overflowX: "hidden" }}>
            {view === "home" && <HomeView obras={obrasVisiveis} onSelect={handleSelect} logs={user?.isVisitor ? [] : logs} isMobile={isMobile} templates={templates} onApplyTemplate={handleApplyTemplate} user={user} onDelete={handleExcluirObra} />}
            {view === "obra-detail" && selObra && canAccessObra(user, selObra.id) && <ObraDetail obra={selObra} onBack={() => setView("home")} onUpdate={handleUpdate} addLog={addLog} isMobile={isMobile} user={user} templates={templates} onApplyTemplate={handleApplyTemplate} />}
            {view === "nova" && (!user?.isVisitor && canEdit(user) && (isAdmin(user) || user?.permissoes?.todas)
              ? <NovaObra onBack={() => setView("home")} templates={templates} onCriar={handleCriar} isMobile={isMobile} />
              : <AccessDenied />)}
            {view === "equipe" && <EquipeView obras={obrasVisiveis} logs={user?.isVisitor ? [] : logs} />}
            {view === "relatorios" && <RelatoriosView obras={obrasVisiveis} />}
            {view === "whatsapp" && (isAdmin(user)
              ? <WhatsAppView obras={obrasVisiveis} contatos={contatos} setContatos={setContatos} agend={agend} setAgend={setAgend} />
              : <AccessDenied />)}
            {view === "usuarios" && (isAdmin(user)
              ? <UsuariosView currentUser={user} obras={obras} isMobile={isMobile} refreshKey={userRefresh} onRefresh={() => setUserRefresh(x => x + 1)} />
              : <AccessDenied />)}
            {view === "config" && (isAdmin(user)
              ? <ConfigView obras={obras} templates={templates} setTemplates={setTemplates} setObras={setObras} logs={logs} setLogs={setLogs} contatos={contatos} setContatos={setContatos} agend={agend} setAgend={setAgend} />
              : <AccessDenied />)}
          </div>
        </div>
      </div>
    </>
  );
}
